import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import Header from '../../layouts/Header';
import { Container, Spinner, Form } from 'react-bootstrap';
import axiosInstance from '../../api/axios';
import OutletSelect from '../../components/OutletSelect';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import XLSX from 'xlsx-js-style';
import IntendImages from '../../assets/img/indentImg.png';

/* ─── constants ──────── */
const STATUS_CONFIG = {
    PENDING: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
    COMPLETED: { label: 'Dispatched', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
    RECEIVED: { label: 'Received', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
    CLOSED: { label: 'Closed', color: '#374151', bg: '#f3f4f6', border: '#9ca3af' },
};

const UPDATABLE_STATUSES = {
    ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
    REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};

const fmt = (iso) =>
    new Date(iso).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
    });

/* ─── date filter presets ──────────────────────────────── */
const DATE_FILTER_OPTIONS = [
    { key: 'TODAY', label: 'Today' },
    { key: 'YESTERDAY', label: 'Yesterday' },
    { key: 'LAST_7_DAYS', label: 'Last 7 Days' },
    { key: 'LAST_30_DAYS', label: 'Last 30 Days' },
    { key: 'LAST_MONTH', label: 'Last Month' },
    { key: 'LAST_3_MONTHS', label: 'Last 3 Months' },
    { key: 'CUSTOM', label: 'Custom Range' },
];

// Formats a Date object as YYYY-MM-DD for the API payload
const formatPayloadDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// For populating <input type="date"> values (needs YYYY-MM-DD)
const formatDateForInput = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Computes { start, end } Date objects for a given preset key
const getDateRangeForOption = (key) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    const end = new Date(today);

    switch (key) {
        case 'TODAY':
            break; // start = end = today

        case 'YESTERDAY':
            start.setDate(start.getDate() - 1);
            end.setDate(end.getDate() - 1);
            break;

        case 'LAST_7_DAYS':
            start.setDate(start.getDate() - 6); // inclusive of today => 7 days total
            break;

        case 'LAST_30_DAYS':
            start.setDate(start.getDate() - 29); // inclusive of today => 30 days total
            break;

        case 'LAST_MONTH': {
            const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const lastDayPrevMonth = new Date(firstDayThisMonth);
            lastDayPrevMonth.setDate(lastDayPrevMonth.getDate() - 1);
            const firstDayPrevMonth = new Date(lastDayPrevMonth.getFullYear(), lastDayPrevMonth.getMonth(), 1);
            return { start: firstDayPrevMonth, end: lastDayPrevMonth };
        }

        case 'LAST_3_MONTHS':
            start.setMonth(start.getMonth() - 3);
            start.setDate(start.getDate() + 1); // inclusive of today
            break;

        default:
            break;
    }
    return { start, end };
};

/* ─── PDF Download (single request) ──────────────────────── */
const handleDownloadPDF = (request) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];
    const upperStatus = (request.status || 'PENDING').toUpperCase();
    const showReceivedQty = request.status === 'RECEIVED' || request.status === 'CLOSED';

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('STOCK REQUEST', pageWidth - 14, 13, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text(`${request.indentNumber || ''}`, pageWidth - 14, 20, { align: 'right' });
    doc.setTextColor(40, 40, 40);

    const startY = 36;
    const col1Label = 14, col1Value = 38, col2Label = 110, col2Value = 128;
    doc.setFontSize(8.5);

    doc.setFont('helvetica', 'normal'); doc.text('Indent :', col1Label, startY);
    doc.setFont('helvetica', 'bold'); doc.text(request.indentNumber || '-', col1Value, startY);

    doc.setFont('helvetica', 'normal'); doc.text('Status :', col1Label, startY + 6);
    doc.setFont('helvetica', 'bold'); doc.text(upperStatus, col1Value, startY + 6);

    doc.setFont('helvetica', 'normal'); doc.text('Date :', col1Label, startY + 12);
    doc.setFont('helvetica', 'bold'); doc.text(formatDate(request.createdAt), col1Value, startY + 12);

    doc.setFont('helvetica', 'normal'); doc.text('Fin. Year :', col1Label, startY + 18);
    doc.setFont('helvetica', 'bold'); doc.text(request.fin_year || '-', col1Value, startY + 18);

    doc.setFont('helvetica', 'normal'); doc.text('Outlet :', col2Label, startY);
    doc.setFont('helvetica', 'bold'); doc.text(request?.outlet?.name || '-', col2Value, startY);

    doc.setFont('helvetica', 'normal'); doc.text('City :', col2Label, startY + 6);
    doc.setFont('helvetica', 'bold'); doc.text(request?.outlet?.city || '-', col2Value, startY + 6);

    doc.setFont('helvetica', 'normal'); doc.text('Address :', col2Label, startY + 12);
    doc.setFont('helvetica', 'bold');
    const addressLines = doc.splitTextToSize(request?.outlet?.address || '-', 60);
    doc.text(addressLines, col2Value, startY + 12);

    const dividerY = startY + 28;
    doc.setDrawColor(200, 200, 200);
    doc.line(14, dividerY, pageWidth - 14, dividerY);

    const items = request.items || [];
    let totalRequested = 0, totalReceived = 0;

    const sortedItems = [...items].sort((a, b) => {
        const mainCompare = (a.rawMaterial?.mainCategoryName || '').localeCompare(b.rawMaterial?.mainCategoryName || '');
        if (mainCompare !== 0) return mainCompare;
        return (a.rawMaterial?.subCategoryName || '').localeCompare(b.rawMaterial?.subCategoryName || '');
    });

    // Build columns dynamically
    const head = [['Sr No', 'Material', 'Main Cat', 'Sub Cat', 'Unit', 'Req Qty']];
    if (showReceivedQty) head[0].push('Rec Qty', 'Balance Qty');

    const tableRows = sortedItems.map((it, index) => {
        const rm = it.rawMaterial || {};
        const requested = Number(it.requestedQuantity || 0);
        const received = Number(it.totalReceivedQuantity || 0);
        const balance = requested - received;
        totalRequested += requested;
        totalReceived += received;

        const row = [
            index + 1,
            rm.name || '-',
            rm.mainCategoryName || '-',
            rm.subCategoryName || '-',
            rm.unit || '-',
            requested.toFixed(2),
        ];
        if (showReceivedQty) row.push(received.toFixed(2), balance.toFixed(2));
        return row;
    });

    const columnStyles = {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'left' },
        2: { halign: 'center', cellWidth: showReceivedQty ? 22 : 28 },
        3: { halign: 'center', cellWidth: showReceivedQty ? 22 : 28 },
        4: { halign: 'center', cellWidth: 14 },
        5: { halign: 'center', cellWidth: showReceivedQty ? 16 : 18 },
    };
    if (showReceivedQty) {
        columnStyles[6] = { halign: 'center', cellWidth: 16 };
        columnStyles[7] = { halign: 'center', cellWidth: 18 };
    }

    autoTable(doc, {
        startY: dividerY + 6,
        head,
        body: tableRows,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
        styles: { fontSize: 8, cellPadding: 2.5, font: 'helvetica', valign: 'middle' },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        columnStyles,
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    let totalsText = `Total Items: ${items.length}   |   Total Requested: ${totalRequested.toFixed(2)}`;
    if (showReceivedQty) {
        totalsText += `   |   Total Received: ${totalReceived.toFixed(2)}   |   Balance: ${(totalRequested - totalReceived).toFixed(2)}`;
    }
    doc.text(totalsText, pageWidth - 14, finalY, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

    doc.save(`Stock-Request-${request.indentNumber || 'Details'}.pdf`);
};

/* ─── Excel style helpers ────────────────────────────────── */
const PRIMARY = '09A5F7'; // sky blue
const WHITE = 'FFFFFF';
const DARK = '1F2937';
const MUTED = '6B7280';
const ALT_ROW = 'F0F7FF';
const TOTAL_BG = 'EEF2FF';
const INDIGO = '4F46E5';
const GREEN_BG = 'D1FAE5';
const GREEN_FG = '059669';
const RED_FG = 'DC2626';

const border = (color = 'FFD1D5DB') => ({
    top: { style: 'thin', color: { rgb: color } },
    bottom: { style: 'thin', color: { rgb: color } },
    left: { style: 'thin', color: { rgb: color } },
    right: { style: 'thin', color: { rgb: color } },
});

const headerCell = (v) => ({
    v, t: 's',
    s: {
        font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 10 },
        fill: { fgColor: { rgb: PRIMARY } },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: border('09A5F7'),
    },
});

const infoLabelCell = (v) => ({
    v, t: 's',
    s: {
        font: { name: 'Arial', bold: true, color: { rgb: MUTED }, sz: 9 },
        fill: { fgColor: { rgb: 'F9FAFB' } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: border(),
    },
});

const infoValueCell = (v) => ({
    v, t: 's',
    s: {
        font: { name: 'Arial', bold: true, color: { rgb: DARK }, sz: 9 },
        fill: { fgColor: { rgb: 'F9FAFB' } },
        alignment: { horizontal: 'left', vertical: 'center' },
        border: border(),
    },
});

const titleCell = (v) => ({
    v, t: 's',
    s: {
        font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 16 },
        fill: { fgColor: { rgb: PRIMARY } },
        alignment: { horizontal: 'center', vertical: 'center' },
    },
});

const dataCell = (v, isAlt, opts = {}) => ({
    v,
    t: typeof v === 'number' ? 'n' : 's',
    z: typeof v === 'number' ? '#,##0.00' : undefined,
    s: {
        font: { name: 'Arial', color: { rgb: opts.color || DARK }, sz: 10, bold: opts.bold || false },
        fill: { fgColor: { rgb: opts.bg || (isAlt ? ALT_ROW : WHITE) } },
        alignment: { horizontal: opts.align || 'left', vertical: 'center', wrapText: true },
        border: border(),
    },
});

const totalCell = (v, opts = {}) => ({
    v,
    t: typeof v === 'number' ? 'n' : 's',
    z: typeof v === 'number' ? '#,##0.00' : undefined,
    s: {
        font: { name: 'Arial', bold: true, color: { rgb: opts.color || INDIGO }, sz: 11 },
        fill: { fgColor: { rgb: TOTAL_BG } },
        alignment: { horizontal: opts.align || 'center', vertical: 'center' },
        border: {
            top: { style: 'medium', color: { rgb: 'C4B5FD' } },
            bottom: { style: 'medium', color: { rgb: 'C4B5FD' } },
            left: { style: 'thin', color: { rgb: 'C4B5FD' } },
            right: { style: 'thin', color: { rgb: 'C4B5FD' } },
        },
    },
});

const blankCell = (bg = PRIMARY) => ({
    v: '', t: 's',
    s: { fill: { fgColor: { rgb: bg } } },
});

/* ─── Apply cells to worksheet ───────────────────────────── */
const setCells = (ws, rowStart, colStart, matrix) => {
    matrix.forEach((row, ri) => {
        row.forEach((cell, ci) => {
            if (cell === null) return;
            const addr = XLSX.utils.encode_cell({ r: rowStart + ri, c: colStart + ci });
            ws[addr] = cell;
        });
    });
};

const setMerge = (ws, r1, c1, r2, c2) => {
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
};

/* ─── Shortage Report Excel (Received < Requested, grouped by outlet) ── */
const handleDownloadShortageExcel = (allRequests) => {
    const outletMap = {};

    allRequests.forEach(req => {
        if (req.status !== 'RECEIVED' && req.status !== 'CLOSED') return;
        const outletId = req?.outlet?._id || req?.outlet || 'unknown';
        const outletName = req?.outlet?.name || 'Unknown Outlet';
        const outletCity = req?.outlet?.city || '—';

        if (!outletMap[outletId]) {
            outletMap[outletId] = { outletName, outletCity, indents: [] };
        }

        const shortageItems = (req.items || [])
            .filter(item => Number(item.totalReceivedQuantity || 0) < Number(item.requestedQuantity || 0))
            .sort((a, b) => {
                const c = (a.rawMaterial?.mainCategory?.name || '').localeCompare(b.rawMaterial?.mainCategory?.name || '');
                return c !== 0 ? c : (a.rawMaterial?.subCategory?.name || '').localeCompare(b.rawMaterial?.subCategory?.name || '');
            });

        if (shortageItems.length > 0) {
            outletMap[outletId].indents.push({
                indentNumber: req.indentNumber || '—',
                createdAt: req.createdAt,
                status: req.status,
                items: shortageItems,
            });
        }
    });

    const outlets = Object.values(outletMap)
        .filter(o => o.indents.length > 0)
        .map(o => ({
            ...o,
            indents: o.indents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
        }));

    if (outlets.length === 0) {
        alert('No shortage items found. All requested quantities were fulfilled!');
        return;
    }

    const LAVENDER_TITLE  = '7C3AED';
    const LAVENDER_LIGHT  = 'F5F3FF';
    const LAVENDER_ALT    = 'EDE9FE';
    const LAVENDER_BORDER = 'C4B5FD';
    const LAVENDER_TEXT   = '6D28D9';
    const LAVENDER_HEADER = '8B5CF6';

    const INDENT_BG     = 'F1F5F9';
    const INDENT_BORDER = 'CBD5E1';
    const INDENT_TEXT   = '1E40AF';

    const wb = XLSX.utils.book_new();

    const lavHeaderCell = (v) => ({
        v, t: 's',
        s: {
            font:      { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 10 },
            fill:      { fgColor: { rgb: LAVENDER_HEADER } },
            alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
            border: {
                top:    { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                bottom: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                left:   { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                right:  { style: 'thin', color: { rgb: LAVENDER_BORDER } },
            },
        },
    });

    const lavTitleCell = (v) => ({
        v, t: 's',
        s: {
            font:      { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 16 },
            fill:      { fgColor: { rgb: LAVENDER_TITLE } },
            alignment: { horizontal: 'center', vertical: 'center' },
        },
    });

    const lavBlankCell = () => ({
        v: '', t: 's',
        s: { fill: { fgColor: { rgb: LAVENDER_TITLE } } },
    });

    const lavSubtitleCell = (v) => ({
        v, t: 's',
        s: {
            font:      { name: 'Arial', color: { rgb: WHITE }, sz: 9 },
            fill:      { fgColor: { rgb: LAVENDER_TITLE } },
            alignment: { horizontal: 'center', vertical: 'center' },
        },
    });

    const indentGroupCell = (v) => ({
        v, t: 's',
        s: {
            font:      { name: 'Arial', bold: true, color: { rgb: INDENT_TEXT }, sz: 10 },
            fill:      { fgColor: { rgb: INDENT_BG } },
            alignment: { horizontal: 'left', vertical: 'center' },
            border: {
                top:    { style: 'medium', color: { rgb: INDENT_BORDER } },
                bottom: { style: 'medium', color: { rgb: INDENT_BORDER } },
                left:   { style: 'medium', color: { rgb: INDENT_BORDER } },
                right:  { style: 'thin',   color: { rgb: INDENT_BORDER } },
            },
        },
    });

    const indentGroupBlank = () => ({
        v: '', t: 's',
        s: {
            fill:  { fgColor: { rgb: INDENT_BG } },
            border: {
                top:    { style: 'medium', color: { rgb: INDENT_BORDER } },
                bottom: { style: 'medium', color: { rgb: INDENT_BORDER } },
                left:   { style: 'thin',   color: { rgb: INDENT_BORDER } },
                right:  { style: 'thin',   color: { rgb: INDENT_BORDER } },
            },
        },
    });

    const shortageCell = (v, isAlt) => ({
        v, t: 'n', z: '#,##0.00',
        s: {
            font:      { name: 'Arial', bold: true, color: { rgb: LAVENDER_TEXT }, sz: 10 },
            fill:      { fgColor: { rgb: isAlt ? LAVENDER_ALT : LAVENDER_LIGHT } },
            alignment: { horizontal: 'center', vertical: 'center' },
            border: {
                top:    { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                bottom: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                left:   { style: 'thin', color: { rgb: LAVENDER_BORDER } },
                right:  { style: 'thin', color: { rgb: LAVENDER_BORDER } },
            },
        },
    });

    const summaryWs = {};
    const summaryLastCol = 5;

    summaryWs[XLSX.utils.encode_cell({ r: 0, c: 0 })] = lavTitleCell('SHORTAGE REPORT — SUMMARY');
    for (let c = 1; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 0, c })] = lavBlankCell();
    setMerge(summaryWs, 0, 0, 0, summaryLastCol);

    summaryWs[XLSX.utils.encode_cell({ r: 1, c: 0 })] = lavSubtitleCell(
        `Generated ${new Date().toLocaleString('en-IN')} · ${allRequests.length} indent(s) analysed`
    );
    for (let c = 1; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 1, c })] = lavBlankCell();
    setMerge(summaryWs, 1, 0, 1, summaryLastCol);

    for (let c = 0; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's' };

    ['Outlet', 'City', 'Indents with Shortages', 'Total Shortage Items', 'Total Shortage Qty', 'Sheet']
        .forEach((h, c) => { summaryWs[XLSX.utils.encode_cell({ r: 3, c })] = lavHeaderCell(h); });

    outlets.forEach((o, idx) => {
        const r = 4 + idx;
        const isAlt = idx % 2 === 1;
        const rowBg = isAlt ? LAVENDER_ALT : LAVENDER_LIGHT;
        const totalShortageItems = o.indents.reduce((s, i) => s + i.items.length, 0);
        const totalShortageQty   = o.indents.reduce((s, i) =>
            s + i.items.reduce((ss, item) =>
                ss + (Number(item.requestedQuantity || 0) - Number(item.totalReceivedQuantity || 0)), 0), 0);
        const sheetName = o.outletName.substring(0, 28).replace(/[\\/:*?[\]]/g, '_');

        [
            dataCell(o.outletName,         isAlt, { align: 'left',   bold: true, bg: rowBg }),
            dataCell(o.outletCity,         isAlt, { align: 'center',             bg: rowBg }),
            dataCell(o.indents.length,     isAlt, { align: 'center', bold: true, color: LAVENDER_TEXT, bg: rowBg }),
            dataCell(totalShortageItems,   isAlt, { align: 'center', bold: true, color: LAVENDER_TEXT, bg: rowBg }),
            shortageCell(totalShortageQty, isAlt),
            dataCell(sheetName,            isAlt, { align: 'center',             color: '2563EB', bg: rowBg }),
        ].forEach((cell, c) => { summaryWs[XLSX.utils.encode_cell({ r, c })] = cell; });
    });

    const summaryFootR = 4 + outlets.length + 1;
    summaryWs[XLSX.utils.encode_cell({ r: summaryFootR, c: 0 })] = {
        v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
        s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
    };
    setMerge(summaryWs, summaryFootR, 0, summaryFootR, summaryLastCol);
    summaryWs['!cols'] = [{ wch: 30 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 28 }];
    summaryWs['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }];
    summaryWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: summaryFootR, c: summaryLastCol } });
    XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

    const lastColIdx = 8;

    outlets.forEach(o => {
        const sheetName = o.outletName.substring(0, 28).replace(/[\\/:*?[\]]/g, '_');
        const ws = {};
        let currentRow = 0;

        ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = lavTitleCell('SHORTAGE REPORT');
        for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavBlankCell();
        setMerge(ws, currentRow, 0, currentRow, lastColIdx);
        currentRow++;

        ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = lavSubtitleCell(`${o.outletName} · ${o.outletCity}`);
        for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavBlankCell();
        setMerge(ws, currentRow, 0, currentRow, lastColIdx);
        currentRow++;

        for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = { v: '', t: 's' };
        currentRow++;

        ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty', 'Rec Qty', 'Shortage']
            .forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavHeaderCell(h); });
        currentRow++;

        o.indents.forEach((indent) => {
            ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = indentGroupCell(
                `  📋  ${indent.indentNumber}     |     ${formatDate(indent.createdAt)}     |     Status: ${indent.status}     |     ${indent.items.length} shortage item${indent.items.length !== 1 ? 's' : ''}`
            );
            for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = indentGroupBlank();
            setMerge(ws, currentRow, 0, currentRow, lastColIdx);
            currentRow++;

            let indentTotalReq = 0, indentTotalRec = 0;
            indent.items.forEach((item, idx) => {
                const rm = item.rawMaterial || {};
                const requested = Number(item.requestedQuantity || 0);
                const received  = Number(item.totalReceivedQuantity  || 0);
                const shortage  = requested - received;
                indentTotalReq += requested;
                indentTotalRec += received;

                const isAlt = idx % 2 === 1;
                const rowBg = isAlt ? LAVENDER_ALT : LAVENDER_LIGHT;

                [
                    dataCell(idx + 1,                          isAlt, { align: 'center',             bg: rowBg }),
                    dataCell(rm.name || '—',                   isAlt, { align: 'left',   bold: true, bg: rowBg }),
                    dataCell(rm.mainCategoryName || '—',     isAlt, { align: 'center',             bg: rowBg }),
                    dataCell(rm.subCategoryName  || '—',     isAlt, { align: 'center',             bg: rowBg }),
                    dataCell(rm.hsn  || '—',                   isAlt, { align: 'center',             bg: rowBg }),
                    dataCell(rm.unit || '—',                   isAlt, { align: 'center',             bg: rowBg }),
                    dataCell(requested, isAlt, { align: 'center', bold: true, color: INDIGO,   bg: rowBg }),
                    dataCell(received,  isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }),
                    shortageCell(shortage, isAlt),
                ].forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = cell; });
                currentRow++;
            });

            [
                totalCell(''), totalCell(''), totalCell(''), totalCell(''), totalCell(''),
                totalCell('SUBTOTAL',              { align: 'right',  color: DARK        }),
                totalCell(indentTotalReq,          { align: 'center'                     }),
                totalCell(indentTotalRec,          { align: 'center', color: GREEN_FG    }),
                totalCell(indentTotalReq - indentTotalRec, { align: 'center', color: LAVENDER_TEXT }),
            ].forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = cell; });
            currentRow++;

            for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = { v: '', t: 's' };
            currentRow++;
        });

        ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = {
            v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
            s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
        };
        setMerge(ws, currentRow, 0, currentRow, lastColIdx);

        ws['!cols'] = [
            { wch: 7  },
            { wch: 28 },
            { wch: 18 },
            { wch: 18 },
            { wch: 12 },
            { wch: 10 },
            { wch: 13 },
            { wch: 13 },
            { wch: 14 },
        ];
        ws['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }];
        ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: currentRow, c: lastColIdx } });

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
    });

    XLSX.writeFile(wb, `Shortage-Report-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
};

/* ─── Excel Download (single request) ──────────────────────── */
const handleDownloadExcel = (request) => {
    const items = request.items || [];
    const showReceivedQty = request.status === 'RECEIVED' || request.status === 'CLOSED';
    const totalCols = showReceivedQty ? 9 : 7;
    const lastColIdx = totalCols - 1;

    const sortedItems = [...items].sort((a, b) => {
        const mc = (a.rawMaterial?.mainCategoryName || '').localeCompare(b.rawMaterial?.mainCategoryName || '');
        return mc !== 0 ? mc : (a.rawMaterial?.subCategoryName || '').localeCompare(b.rawMaterial?.subCategoryName || '');
    });

    const ws = {};
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 999, c: lastColIdx } });

    ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = titleCell('STOCK REQUEST');
    for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 0, c })] = blankCell();
    setMerge(ws, 0, 0, 0, lastColIdx);

    ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
        v: request.indentNumber || '', t: 's',
        s: { font: { name: 'Arial', bold: false, color: { rgb: WHITE }, sz: 9 }, fill: { fgColor: { rgb: PRIMARY } }, alignment: { horizontal: 'center', vertical: 'center' } },
    };
    for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 1, c })] = blankCell();
    setMerge(ws, 1, 0, 1, lastColIdx);

    for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } };

    const half = Math.floor(lastColIdx / 2);
    const infoLeft = [
        ['Indent Number', request.indentNumber || '-'],
        ['Status', request.status || '-'],
        ['Date', formatDate(request.createdAt)],
        ['Financial Year', request.fin_year || '-'],
    ];
    const infoRight = [
        ['Outlet', request?.outlet?.name || '-'],
        ['City', request?.outlet?.city || '-'],
        ['Address', request?.outlet?.address || '-'],
        ['', ''],
    ];

    infoLeft.forEach(([label, value], i) => {
        const r = 3 + i;
        ws[XLSX.utils.encode_cell({ r, c: 0 })] = infoLabelCell(label);
        ws[XLSX.utils.encode_cell({ r, c: 1 })] = infoValueCell(value);
        for (let c = 2; c <= half; c++) ws[XLSX.utils.encode_cell({ r, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'F9FAFB' } }, border: border() } };
        setMerge(ws, r, 1, r, half);
    });
    infoRight.forEach(([label, value], i) => {
        const r = 3 + i;
        ws[XLSX.utils.encode_cell({ r, c: half + 1 })] = infoLabelCell(label);
        ws[XLSX.utils.encode_cell({ r, c: half + 2 })] = infoValueCell(value);
        for (let c = half + 3; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'F9FAFB' } }, border: border() } };
        setMerge(ws, r, half + 2, r, lastColIdx);
    });

    for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 7, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'E5E7EB' } } } };

    const headers = ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty'];
    if (showReceivedQty) headers.push('Rec Qty', 'Balance Qty');
    headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: 8, c })] = headerCell(h); });

    let totalRequested = 0, totalReceived = 0;
    sortedItems.forEach((it, idx) => {
        const rm = it.rawMaterial || {};
        const requested = Number(it.requestedQuantity || 0);
        const received = Number(it.totalReceivedQuantity || 0);
        const balance = requested - received;
        totalRequested += requested;
        totalReceived += received;
        const r = 9 + idx;
        const isAlt = idx % 2 === 1;

        const rowCells = [
            dataCell(idx + 1, isAlt, { align: 'center' }),
            dataCell(rm.name || '-', isAlt),
            dataCell(rm.mainCategoryName || '-', isAlt, { align: 'center' }),
            dataCell(rm.subCategoryName || '-', isAlt, { align: 'center' }),
            dataCell(rm.hsn || '-', isAlt, { align: 'center' }),
            dataCell(rm.unit || '-', isAlt, { align: 'center' }),
            dataCell(requested, isAlt, { align: 'center', bold: true, color: INDIGO }),
        ];
        if (showReceivedQty) {
            rowCells.push(dataCell(received, isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }));
            rowCells.push(dataCell(balance, isAlt, { align: 'center', bold: true, color: balance > 0 ? RED_FG : GREEN_FG }));
        }
        rowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r, c })] = cell; });
    });

    const totalsR = 9 + sortedItems.length;
    const totalRowCells = [
        totalCell('', { align: 'center' }),
        totalCell('', { align: 'center' }),
        totalCell('', { align: 'center' }),
        totalCell('', { align: 'center' }),
        totalCell('', { align: 'center' }),
        totalCell('TOTAL', { align: 'right', color: DARK }),
        totalCell(totalRequested, { align: 'center' }),
    ];
    if (showReceivedQty) {
        totalRowCells.push(totalCell(totalReceived, { align: 'center', color: GREEN_FG }));
        totalRowCells.push(totalCell(totalRequested - totalReceived, { align: 'center', color: RED_FG }));
    }
    totalRowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: totalsR, c })] = cell; });

    const footR = totalsR + 2;
    ws[XLSX.utils.encode_cell({ r: footR, c: 0 })] = {
        v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
        s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
    };
    setMerge(ws, footR, 0, footR, lastColIdx);

    ws['!cols'] = [
        { wch: 7 },
        { wch: 30 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 10 },
        { wch: 13 },
        ...(showReceivedQty ? [{ wch: 13 }, { wch: 13 }] : []),
    ];

    ws['!rows'] = [
        { hpt: 30 },
        { hpt: 18 },
        { hpt: 6 },
        { hpt: 18 }, { hpt: 18 }, { hpt: 18 }, { hpt: 18 },
        { hpt: 6 },
        { hpt: 22 },
    ];

    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: footR, c: lastColIdx } });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Request');
    XLSX.writeFile(wb, `Stock-Request-${request.indentNumber || 'Details'}.xlsx`);
};

/* ─── Combined PDF (aggregated) ──────────────────────────── */
const handleDownloadCombinedPDF = (allRequests) => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const primaryColor = [9, 165, 247];

    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, pageWidth, 28, 'F');

    try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.text('Cumulative Stock Request', (pageWidth / 2) + 20, 16, { align: 'center' });
    doc.setTextColor(40, 40, 40);

    const materialMap = {};
    allRequests.forEach(req => {
        (req.items || []).forEach(item => {
            const rm = item.rawMaterial || {};
            const id = rm._id || rm.name || 'unknown';
            if (!materialMap[id]) {
                materialMap[id] = {
                    name: rm.name || '—',
                    mainCat: rm.mainCategoryName || '—',
                    subCat: rm.subCategoryName || '—',
                    unit: rm.unit || '—',
                    totalRequested: 0,
                    totalReceived: 0,
                };
            }
            materialMap[id].totalRequested += Number(item.requestedQuantity || 0);
            materialMap[id].totalReceived += Number(item.totalReceivedQuantity || 0);
        });
    });

    const hasReceivedData = allRequests.some(r => r.status === 'RECEIVED' || r.status === 'CLOSED');

    const rows = Object.values(materialMap)
        .sort((a, b) => {
            const c = a.mainCat.localeCompare(b.mainCat);
            return c !== 0 ? c : a.subCat.localeCompare(b.subCat);
        })
        .map((m, idx) => {
            const balance = m.totalRequested - m.totalReceived;
            const row = [
                idx + 1,
                m.name,
                `${m.mainCat} > ${m.subCat}`,
                Number(m.totalRequested).toFixed(2),
                m.unit,
            ];
            if (hasReceivedData) row.push(Number(m.totalReceived).toFixed(2), balance.toFixed(2));
            return row;
        });

    const head = [['Sr No', 'Material', 'Category', 'Req. Qty', 'Unit']];
    if (hasReceivedData) head[0].push('Rec. Qty', 'Balance Qty');

    const columnStyles = {
        0: { halign: 'center', cellWidth: 10 },
        1: { halign: 'left', cellWidth: hasReceivedData ? 48 : 60 },
        2: { halign: 'left', cellWidth: hasReceivedData ? 48 : 60 },
        3: { halign: 'center', cellWidth: hasReceivedData ? 20 : 25 },
        4: { halign: 'center', cellWidth: hasReceivedData ? 18 : 25 },
    };
    if (hasReceivedData) {
        columnStyles[5] = { halign: 'center', cellWidth: 20 };
        columnStyles[6] = { halign: 'center', cellWidth: 20 };
    }

    autoTable(doc, {
        startY: 34,
        head,
        body: rows,
        theme: 'grid',
        headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 8, cellPadding: 2, font: 'helvetica' },
        alternateRowStyles: { fillColor: [245, 250, 255] },
        columnStyles,
    });

    const finalY = doc.lastAutoTable.finalY + 8;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text(`Materials: ${rows.length}`, pageWidth - 14, finalY, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
    doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

    doc.save(`Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
};

/* ─── Combined Excel (aggregated) ──────────────────────────── */
const handleDownloadCombinedExcel = (allRequests) => {
    const materialMap = {};
    allRequests.forEach(req => {
        (req.items || []).forEach(item => {
            const rm = item.rawMaterial || {};
            const id = rm._id || rm.name || 'unknown';
            if (!materialMap[id]) {
                materialMap[id] = {
                    name: rm.name || '—',
                    mainCat: rm.mainCategoryName || '—',
                    subCat: rm.subCategoryName || '—',
                    unit: rm.unit || '—',
                    hsn: rm.hsn || '—',
                    totalRequested: 0,
                    totalReceived: 0,
                };
            }
            materialMap[id].totalRequested += Number(item.requestedQuantity || 0);
            materialMap[id].totalReceived += Number(item.totalReceivedQuantity || 0);
        });
    });

    const hasReceivedData = allRequests.some(r => r.status === 'RECEIVED' || r.status === 'CLOSED');
    const totalCols = hasReceivedData ? 9 : 7;
    const lastColIdx = totalCols - 1;

    const sorted = Object.values(materialMap).sort((a, b) => {
        const c = a.mainCat.localeCompare(b.mainCat);
        return c !== 0 ? c : a.subCat.localeCompare(b.subCat);
    });

    const ws = {};
    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 999, c: lastColIdx } });

    ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = titleCell('CUMULATIVE STOCK REQUEST');
    for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 0, c })] = blankCell();
    setMerge(ws, 0, 0, 0, lastColIdx);

    ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
        v: `${allRequests.length} Request${allRequests.length !== 1 ? 's' : ''} · Generated ${new Date().toLocaleString('en-IN')}`,
        t: 's',
        s: { font: { name: 'Arial', color: { rgb: WHITE }, sz: 9 }, fill: { fgColor: { rgb: PRIMARY } }, alignment: { horizontal: 'center', vertical: 'center' } },
    };
    for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 1, c })] = blankCell();
    setMerge(ws, 1, 0, 1, lastColIdx);

    for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's' };

    const headers = ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty'];
    if (hasReceivedData) headers.push('Rec Qty', 'Balance Qty');
    headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: 3, c })] = headerCell(h); });

    let totalRequested = 0, totalReceived = 0;
    const ORANGE = 'EA580C';

    sorted.forEach((m, idx) => {
        const balance = m.totalRequested - m.totalReceived;
        const isShortage = hasReceivedData && m.totalReceived < m.totalRequested;
        totalRequested += m.totalRequested;
        totalReceived += m.totalReceived;
        const r = 4 + idx;
        const isAlt = idx % 2 === 1;

        const rowBg = isShortage ? (isAlt ? 'FFF3E0' : 'FFF7ED') : undefined;
        const shortageIndicator = isShortage ? '⚠ ' : '';

        const rowCells = [
            dataCell(idx + 1, isAlt, { align: 'center', bg: rowBg }),
            dataCell(shortageIndicator + m.name, isAlt, { bold: isShortage, color: isShortage ? ORANGE : DARK, bg: rowBg }),
            dataCell(m.mainCat, isAlt, { align: 'center', bg: rowBg }),
            dataCell(m.subCat, isAlt, { align: 'center', bg: rowBg }),
            dataCell(m.hsn, isAlt, { align: 'center', bg: rowBg }),
            dataCell(m.unit, isAlt, { align: 'center', bg: rowBg }),
            dataCell(m.totalRequested, isAlt, { align: 'center', bold: true, color: INDIGO, bg: rowBg }),
        ];
        if (hasReceivedData) {
            rowCells.push(dataCell(m.totalReceived, isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }));
            rowCells.push(
                isShortage
                    ? {
                        v: balance, t: 'n', z: '#,##0.00',
                        s: {
                            font: { name: 'Arial', bold: true, color: { rgb: ORANGE }, sz: 10 },
                            fill: { fgColor: { rgb: isAlt ? 'FFE0B2' : 'FFF3E0' } },
                            alignment: { horizontal: 'center', vertical: 'center' },
                            border: {
                                top: { style: 'thin', color: { rgb: 'FED7AA' } },
                                bottom: { style: 'thin', color: { rgb: 'FED7AA' } },
                                left: { style: 'thin', color: { rgb: 'FED7AA' } },
                                right: { style: 'thin', color: { rgb: 'FED7AA' } },
                            },
                        },
                    }
                    : dataCell(balance, isAlt, { align: 'center', bold: true, color: balance > 0 ? RED_FG : GREEN_FG })
            );
        }
        rowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r, c })] = cell; });
    });

    const totalsR = 4 + sorted.length;
    const totalRowCells = [
        totalCell(''), totalCell(''), totalCell(''), totalCell(''), totalCell(''),
        totalCell('TOTAL', { align: 'right', color: DARK }),
        totalCell(totalRequested, { align: 'center' }),
    ];
    if (hasReceivedData) {
        totalRowCells.push(totalCell(totalReceived, { align: 'center', color: GREEN_FG }));
        totalRowCells.push(totalCell(totalRequested - totalReceived, { align: 'center', color: RED_FG }));
    }
    totalRowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: totalsR, c })] = cell; });

    const footR = totalsR + 2;
    ws[XLSX.utils.encode_cell({ r: footR, c: 0 })] = {
        v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
        s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
    };
    setMerge(ws, footR, 0, footR, lastColIdx);

    ws['!cols'] = [
        { wch: 7 },
        { wch: 32 },
        { wch: 20 },
        { wch: 20 },
        { wch: 12 },
        { wch: 10 },
        { wch: 13 },
        ...(hasReceivedData ? [{ wch: 13 }, { wch: 13 }] : []),
    ];

    ws['!rows'] = [
        { hpt: 30 },
        { hpt: 18 },
        { hpt: 6 },
        { hpt: 22 },
    ];

    ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: footR, c: lastColIdx } });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cumulative');
    XLSX.writeFile(wb, `Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
};

/* ─── Download Dropdown ─────────────────────────────────── */
const DownloadDropdown = ({ onPDF, onExcel, label = '📄 Download', isDark }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button
                onClick={() => setOpen(o => !o)}
                style={{
                    background: '#fef3c7',
                    border: '1px solid #fcd34d',
                    borderRadius: 7,
                    padding: '6px 13px',
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#d97706',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 5,
                    fontFamily: 'inherit',
                    transition: 'background .15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
                onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
            >
                {label}
                <span style={{
                    fontSize: 9,
                    display: 'inline-block',
                    transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform .2s',
                }}>▼</span>
            </button>

            {open && (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 4px)',
                    background: isDark ? '#1e293b' : '#fff',
                    border: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
                    borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    minWidth: 150,
                    zIndex: 200,
                    overflow: 'hidden',
                }}>
                    <button
                        onClick={() => { onPDF(); setOpen(false); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            width: '100%', background: 'none', border: 'none',
                            padding: '9px 14px', fontSize: 13, fontWeight: 600,
                            color: isDark ? '#f3f4f6' : '#374151',
                            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                            borderBottom: `1px solid ${isDark ? '#2d3748' : '#f3f4f6'}`,
                            transition: 'background .12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? '#252d3d' : '#fef3c7'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <span>📄</span> Download PDF
                    </button>
                    <button
                        onClick={() => { onExcel(); setOpen(false); }}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            width: '100%', background: 'none', border: 'none',
                            padding: '9px 14px', fontSize: 13, fontWeight: 600,
                            color: isDark ? '#f3f4f6' : '#374151',
                            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                            transition: 'background .12s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = isDark ? '#252d3d' : '#d1fae5'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                        <span>📊</span> Download Excel
                    </button>
                </div>
            )}
        </div>
    );
};

/* ─── Date Range Dropdown ─────────────────────────────────── */
const DateRangeDropdown = ({
    dateFilterKey, startDate, endDate,
    onPresetSelect, onCustomDateChange, isDark,
}) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';
    const cardBg = isDark ? '#1e293b' : '#fff';

    const selectedLabel = DATE_FILTER_OPTIONS.find(o => o.key === dateFilterKey)?.label || 'Select Range';

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <Form.Label style={{ color: textMuted, marginBottom: 6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <i className="bi bi-calendar-range me-2"></i>Date Range
            </Form.Label>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', textAlign: 'left',
                    background: isDark ? '#252d3d' : '#f9fafb',
                    border: `1px solid ${borderCol}`, borderRadius: 8,
                    padding: '8px 12px', fontSize: 13, color: textMain,
                    fontFamily: 'inherit', cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}
            >
                <span>
                    {selectedLabel}
                    {dateFilterKey === 'CUSTOM' && startDate && endDate && (
                        <span style={{ color: textMuted, marginLeft: 6, fontSize: 11 }}>
                            ({formatPayloadDate(startDate)} – {formatPayloadDate(endDate)})
                        </span>
                    )}
                </span>
                <span style={{ fontSize: 9, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                    background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 8,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300, overflow: 'hidden',
                    minWidth: 260,
                }}>
                    {DATE_FILTER_OPTIONS.filter(o => o.key !== 'CUSTOM').map(opt => (
                        <button
                            type="button"
                            key={opt.key}
                            onClick={() => { onPresetSelect(opt.key); setOpen(false); }}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                background: dateFilterKey === opt.key ? (isDark ? '#252d3d' : '#eef2ff') : 'transparent',
                                border: 'none', padding: '9px 14px', fontSize: 13, fontWeight: 600,
                                color: dateFilterKey === opt.key ? '#6366f1' : textMain,
                                cursor: 'pointer', fontFamily: 'inherit',
                                borderBottom: `1px solid ${isDark ? '#2d3748' : '#f3f4f6'}`,
                            }}
                        >
                            {opt.label}
                        </button>
                    ))}

                    <div style={{ padding: '10px 14px', background: isDark ? '#1a2132' : '#fafafa' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: textMuted, marginBottom: 8 }}>Custom Range</div>
                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                type="date"
                                value={formatDateForInput(startDate)}
                                max={formatDateForInput(endDate) || formatDateForInput(new Date())}
                                onChange={(e) => onCustomDateChange('start', e.target.value)}
                                style={{ flex: 1, background: isDark ? '#252d3d' : '#fff', border: `1px solid ${borderCol}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: textMain, fontFamily: 'inherit', outline: 'none' }}
                            />
                            <input
                                type="date"
                                value={formatDateForInput(endDate)}
                                min={formatDateForInput(startDate)}
                                max={formatDateForInput(new Date())}
                                onChange={(e) => onCustomDateChange('end', e.target.value)}
                                style={{ flex: 1, background: isDark ? '#252d3d' : '#fff', border: `1px solid ${borderCol}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: textMain, fontFamily: 'inherit', outline: 'none' }}
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

/* ─── StatusPill ─────────────────────────────────────────── */
const StatusPill = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
    return (
        <span style={{
            padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
            letterSpacing: '0.04em', textTransform: 'uppercase',
            color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
            whiteSpace: 'nowrap',
        }}>
            {cfg.label}
        </span>
    );
};

/* ─── Items Expandable ───────────────────────────────────── */
const ItemsRow = ({ items, status }) => {
    const [open, setOpen] = useState(false);
    const showReceivedCols = status === 'RECEIVED' || status === 'CLOSED';

    return (
        <div style={{ marginTop: 10 }}>
            <button onClick={() => setOpen(o => !o)} style={{
                background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                display: 'inline-flex', alignItems: 'center', gap: 6,
                color: '#6366f1', fontSize: 13, fontWeight: 600,
            }}>
                <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 20, height: 20, borderRadius: 6,
                    background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800,
                }}>{items.length}</span>
                Item{items.length !== 1 ? 's' : ''}
                <span style={{ fontSize: 9, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
            </button>

            {open && (
                <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: showReceivedCols ? '2.5fr 1fr 1fr 1fr 130px 130px 130px' : '2.5fr 1fr 1fr 1fr 140px',
                        background: '#f9fafb', padding: '7px 14px', borderBottom: '1px solid #e5e7eb',
                        fontSize: 11, fontWeight: 700, color: '#6b7280',
                        letterSpacing: '0.05em', textTransform: 'uppercase',
                    }}>
                        <span>Material</span>
                        <span>Category</span>
                        <span>HSN</span>
                        <span>Unit</span>
                        <span>Requested Qty</span>
                        {showReceivedCols && <span>Received Qty</span>}
                        {showReceivedCols && <span>Balance Qty</span>}
                    </div>

                    {items.map((item, idx) => {
                        const rm = item.rawMaterial || {};
                        const requested = Number(item.requestedQuantity || 0);
                        const received = Number(item.totalReceivedQuantity || 0);
                        const balance = requested - received;
                        return (
                            <div key={item._id || idx} style={{
                                display: 'grid',
                                gridTemplateColumns: showReceivedCols ? '2.5fr 1fr 1fr 1fr 130px 130px 130px' : '2.5fr 1fr 1fr 1fr 130px',
                                padding: '9px 14px', alignItems: 'center',
                                background: idx % 2 === 0 ? '#fff' : '#fafafa',
                                borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
                                fontSize: 13,
                            }}>
                                <div>
                                    <div style={{ fontWeight: 600, color: '#111827' }}>{rm.name || '—'}</div>
                                    {rm.description && (
                                        <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
                                            {rm.description}
                                        </div>
                                    )}
                                </div>
                                <span style={{ color: '#373b41', fontSize: 12 }}>
                                    {rm?.subCategoryName}
                                    <div style={{ color: '#6b7280', fontSize: 12 }}>{rm?.mainCategoryName || '—'}</div>
                                </span>
                                <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{rm?.hsn || '—'}</span>
                                <span style={{ color: '#6b7280', fontSize: 12 }}>{rm?.unit || '—'}</span>
                                <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
                                    {requested.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
                                </span>
                                {showReceivedCols && (
                                    <span style={{ textAlign: 'left', fontWeight: 700, color: '#059669', fontSize: 14 }}>
                                        {received.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
                                    </span>
                                )}
                                {showReceivedCols && (
                                    <span style={{ textAlign: 'left', fontWeight: 700, color: balance > 0 ? '#dc2626' : '#059669', fontSize: 14 }}>
                                        {balance.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

/* ─── Update Modal ───────────────────────────────────────── */
const UpdateModal = ({ request, onHide, onUpdated, accessToken }) => {
    const [status, setStatus] = useState('ACCEPTED');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const alreadyUpdated = request?.status === 'ACCEPTED' || request?.status === 'REJECTED';

    const handleSave = async () => {
        setSaving(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            await axiosInstance.put(`stock-requisition/update-status`, { _id: request._id, status }, { headers });
            onUpdated(request._id, status);
            onHide();
        } catch (error) {
            if (error?.response) {
                let errorMessage = '';
                if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
                else if (error.response?.status === 400) errorMessage = error?.response?.data?.message || error?.response?.data?.error;
                else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
                else if (error.response?.status === 500) errorMessage = error?.response?.data?.error || error?.response?.data?.message;
                setError(errorMessage || 'Failed to update. Please try again.');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 1050,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={onHide}>
            <div style={{
                background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
                margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
            }} onClick={e => e.stopPropagation()}>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
                    <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Update Stock Request</span>
                    <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0 }}>×</button>
                </div>

                <div style={{ padding: '20px' }}>
                    <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Indent Number</div>
                            <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>{request.indentNumber}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</div>
                            <StatusPill status={request.status} />
                        </div>
                    </div>

                    {alreadyUpdated ? (
                        <div>
                            <div style={{ padding: '16px', borderRadius: 9, background: '#f9fafb', border: '1px solid #e5e7eb', textAlign: 'center', color: '#6b7280', fontSize: 13, lineHeight: 1.6 }}>
                                This request has already been <strong style={{ color: '#374151' }}>{request.status.toLowerCase()}</strong>. You can still change the status below.
                            </div>
                            <div className='mt-3' style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`, background: status === key ? cfg.bg : '#fff', transition: 'all .15s', textAlign: 'center' }}>
                                        <input type="radio" name="status" value={key} checked={status === key} onChange={() => setStatus(key)} style={{ accentColor: cfg.color, width: 15, height: 15 }} />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#374151' }}>
                                Set Status <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(choose one)</span>
                            </div>
                            <div style={{ display: 'flex', gap: 10 }}>
                                {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
                                    <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`, background: status === key ? cfg.bg : '#fff', transition: 'all .15s', textAlign: 'center' }}>
                                        <input type="radio" name="status" value={key} checked={status === key} onChange={() => setStatus(key)} style={{ accentColor: cfg.color, width: 15, height: 15 }} />
                                        <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}

                    {error && (
                        <div style={{ marginTop: 12, padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>{error}</div>
                    )}
                </div>

                <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button onClick={onHide} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>
                        {alreadyUpdated ? 'Close' : 'Cancel'}
                    </button>
                    <button onClick={handleSave} disabled={saving} style={{ background: saving ? '#a5b4fc' : '#6366f1', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 700, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
                        {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/* ─── Request Card ───────────────────────────────────────── */
const RequestCard = ({ req, onUpdate, isDark }) => {
    const cardBg = isDark ? '#1a2132' : '#fff';
    const border = isDark ? '#2d3748' : '#e5e7eb';
    const text = isDark ? '#f3f4f6' : '#111827';
    const muted = isDark ? '#9ca3af' : '#6b7280';
    const isAccepted = req.status === 'ACCEPTED';

    return (
        <div style={{
            background: cardBg, border: `1px solid ${border}`, borderRadius: 10,
            padding: '16px 18px', marginBottom: 12,
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
            transition: 'box-shadow .2s',
        }}
            onMouseEnter={e => e.currentTarget.style.boxShadow = isDark ? '0 4px 18px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.09)'}
            onMouseLeave={e => e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#6366f1' }}>{req.indentNumber}</span>
                        <StatusPill status={req.status === 'COMPLETED' ? 'COMPLETED' : req.status} />
                        <span style={{ fontSize: 11, color: muted, background: isDark ? '#252d3d' : '#f3f4f6', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
                            FY {req.fin_year}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
                        Outlet: <span style={{ color: text, fontWeight: 500 }}>{req?.outlet?.name || req?.outlet?._id || '—'}, {req?.outlet?.city}</span>
                    </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
                        <div>Raised on: <span style={{ color: text }}>{fmt(req.createdAt)}</span></div>
                    </div>

                    {req.status !== 'COMPLETED' && req.status !== 'RECEIVED' && req.status !== 'CLOSED' && (
                        <button onClick={() => onUpdate(req)} style={{
                            background: isAccepted ? '#d1fae5' : '#f5f3ff',
                            border: `1px solid ${isAccepted ? '#6ee7b7' : '#ddd6fe'}`,
                            borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 700,
                            color: isAccepted ? '#059669' : '#7c3aed',
                            cursor: 'pointer', whiteSpace: 'nowrap',
                            display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', transition: 'background .15s',
                        }}
                            onMouseEnter={e => e.currentTarget.style.background = isAccepted ? '#a7f3d0' : '#ede9fe'}
                            onMouseLeave={e => e.currentTarget.style.background = isAccepted ? '#d1fae5' : '#f5f3ff'}
                        >
                            {isAccepted ? '📦 Transfer Stock' : '✎ Update'}
                        </button>
                    )}

                    {/* Download Dropdown per card */}
                    <DownloadDropdown
                        label="📄 Download"
                        isDark={isDark}
                        onPDF={() => handleDownloadPDF(req)}
                        onExcel={() => handleDownloadExcel(req)}
                    />
                </div>
            </div>

            <ItemsRow items={req.items || []} status={req.status} />
        </div>
    );
};

/* ─── Pagination ─────────────────────────────────────────── */
const Pagination = ({ currentPage, totalPages, onPageChange, isDark }) => {
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else if (currentPage <= 3) {
            pages.push(1, 2, 3, 4, '...', totalPages);
        } else if (currentPage >= totalPages - 2) {
            pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
        } else {
            pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
        }
        return pages;
    };

    const btnBase = {
        border: `1px solid ${borderCol}`, borderRadius: 7, padding: '6px 12px',
        fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
            <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...btnBase, background: isDark ? '#1e293b' : '#fff', color: currentPage === 1 ? textMuted : textMain, opacity: currentPage === 1 ? 0.5 : 1 }}>← Previous</button>
            {getPageNumbers().map((page, idx) => (
                page === '...'
                    ? <span key={`e-${idx}`} style={{ color: textMuted, padding: '0 4px' }}>...</span>
                    : <button key={page} onClick={() => onPageChange(page)} style={{ ...btnBase, background: currentPage === page ? '#6366f1' : (isDark ? '#1e293b' : '#fff'), border: `1px solid ${currentPage === page ? '#6366f1' : borderCol}`, color: currentPage === page ? '#fff' : textMain, fontWeight: currentPage === page ? 700 : 600, minWidth: 36 }}>{page}</button>
            ))}
            <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...btnBase, background: isDark ? '#1e293b' : '#fff', color: currentPage === totalPages ? textMuted : textMain, opacity: currentPage === totalPages ? 0.5 : 1 }}>Next →</button>
        </div>
    );
};

/* ─── Main Component ─────────────────────────────────────── */
const StocksRequest = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);
    const accessToken = localStorage.getItem('authToken');

    const [stockRequests, setStockRequests] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [updateTarget, setUpdateTarget] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [statusCounts, setStatusCounts] = useState({});

    // ── Date filter state (defaults to Last 30 Days) ──
    const [dateFilterKey, setDateFilterKey] = useState('LAST_30_DAYS');
    const initialRange = getDateRangeForOption('LAST_30_DAYS');
    const [startDate, setStartDate] = useState(initialRange.start); // Date object
    const [endDate, setEndDate] = useState(initialRange.end);       // Date object

    const navigate = useNavigate();

    const isDark = skin === 'dark';
    const pageBg = isDark ? '#111827' : '#f3f4f6';
    const cardBg = isDark ? '#1a2132' : '#fff';
    const textMain = isDark ? '#f3f4f6' : '#111827';
    const textMuted = isDark ? '#9ca3af' : '#6b7280';
    const borderCol = isDark ? '#2d3748' : '#e5e7eb';

    const getStockRequests = async (page = 1, filters = {}) => {
        setLoading(true); setError('');
        try {
            const headers = { Authorization: `Bearer ${accessToken}` };
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', 20);

            const outletId = filters.outlet?.value || filters.outlet?._id || selectedOutlet?.value || selectedOutlet?._id || '';
            if (outletId) params.append('outlet', outletId);

            const status = filters.status !== undefined ? filters.status : statusFilter;
            if (status && status !== 'ALL') params.append('status', status);

            const start = filters.startDate !== undefined ? filters.startDate : startDate;
            const end = filters.endDate !== undefined ? filters.endDate : endDate;
            if (start) params.append('startDate', formatPayloadDate(start));
            if (end) params.append('endDate', formatPayloadDate(end));

            const res = await axiosInstance.get(`stock-requisition?${params.toString()}`, { headers });
            const responseData = res?.data?.data || {};
            const requests = responseData.data || [];
            const pagination = responseData.pagination || {};
            const counts = responseData.statusCounts || {};

            const sortedData = requests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
            setStockRequests(sortedData);
            setCurrentPage(pagination.currentPage || 1);
            setTotalPages(pagination.totalPages || 1);
            setTotalRecords(pagination.totalRecords || 0);
            setStatusCounts(counts);
        } catch (err) {
            setStockRequests([]);
            setError('There are no stock requests at the moment.');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateClick = (req) => {
        if (req.status === 'ACCEPTED') {
            navigate('/purchase/stock-transfer', { state: { request: req } });
        } else {
            setUpdateTarget(req);
        }
    };

    useEffect(() => { getStockRequests(1, { startDate, endDate }); }, []);

    const handleOutletChange = (option) => { setSelectedOutlet(option); setCurrentPage(1); getStockRequests(1, { outlet: option }); };
    const handleStatusChange = (newStatus) => { setStatusFilter(newStatus); setCurrentPage(1); getStockRequests(1, { status: newStatus }); };

    const handlePresetSelect = (key) => {
        setDateFilterKey(key);
        if (key === 'CUSTOM') return; // wait for user to pick custom dates
        const { start, end } = getDateRangeForOption(key);
        setStartDate(start);
        setEndDate(end);
        setCurrentPage(1);
        getStockRequests(1, { startDate: start, endDate: end });
    };

    const handleCustomDateChange = (type, value) => {
        // value comes from <input type="date"> as 'YYYY-MM-DD'
        const parsed = value ? new Date(value + 'T00:00:00') : null;
        setDateFilterKey('CUSTOM');
        if (type === 'start') {
            setStartDate(parsed);
            setCurrentPage(1);
            getStockRequests(1, { startDate: parsed, endDate });
        } else {
            setEndDate(parsed);
            setCurrentPage(1);
            getStockRequests(1, { startDate, endDate: parsed });
        }
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) { setCurrentPage(page); getStockRequests(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }
    };
    const handleUpdated = () => getStockRequests(currentPage);
    const getAllCount = () => Object.values(statusCounts).reduce((sum, count) => sum + count, 0);

    const showCumulativeBtn = (statusFilter === 'PENDING' || statusFilter === 'ACCEPTED') && stockRequests.length > 0;

    return (
        <React.Fragment>
            <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
            <Header onSkin={setSkin} />

            {updateTarget && (
                <UpdateModal request={updateTarget} onHide={() => setUpdateTarget(null)} onUpdated={handleUpdated} accessToken={accessToken} />
            )}

            <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}
                style={{ minHeight: '100vh', background: pageBg, fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
                <Container fluid>

                    {/* ── Page Header ── */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
                        <div>
                            <h4 style={{ fontWeight: 800, fontSize: 21, margin: 0, color: textMain, letterSpacing: '-0.01em' }}>Stock Requests</h4>
                            <p style={{ color: textMuted, margin: '3px 0 0', fontSize: 13 }}>Manage and track material requisitions from outlets</p>
                        </div>
                        <button onClick={() => getStockRequests(currentPage)} disabled={loading} style={{
                            background: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
                            padding: '9px 18px', fontWeight: 700, fontSize: 13,
                            cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
                            display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
                        }}>
                            {loading ? <><Spinner animation="border" size="sm" /> Refreshing…</> : <><span style={{ fontSize: 15 }}>↺</span> Refresh</>}
                        </button>
                    </div>

                    {/* ── Filters ── */}
                    <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '16px', marginBottom: 14 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.3fr 1.5fr', gap: 12 }}>
                            <Form.Group controlId="outletSelect" style={{ margin: 0 }}>
                                <OutletSelect value={selectedOutlet} onChange={handleOutletChange} required={false} />
                            </Form.Group>

                            <DateRangeDropdown
                                dateFilterKey={dateFilterKey}
                                startDate={startDate}
                                endDate={endDate}
                                onPresetSelect={handlePresetSelect}
                                onCustomDateChange={handleCustomDateChange}
                                isDark={isDark}
                            />

                            <div>
                                <Form.Label style={{ color: textMuted, marginBottom: 6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                    <i className="bi bi-search me-2"></i>Search
                                </Form.Label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: isDark ? '#252d3d' : '#f9fafb', border: `1px solid ${borderCol}`, borderRadius: 8, padding: '8px 12px' }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by indent no., outlet, or material…"
                                        style={{ border: 'none', background: 'transparent', flex: 1, outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit' }} />
                                    {searchTerm && (
                                        <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Status Filter Chips + Cumulative Download ── */}
                    {!loading && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                {[{ key: 'ALL', label: 'All' }, ...Object.keys(STATUS_CONFIG).map(k => ({ key: k, label: STATUS_CONFIG[k].label }))].map(({ key, label }) => {
                                    const count = key === 'ALL' ? getAllCount() : (statusCounts[key.toLowerCase()] || 0);
                                    const cfg = STATUS_CONFIG[key] || {};
                                    const active = statusFilter === key;
                                    return (
                                        <button key={key} onClick={() => handleStatusChange(key)} style={{
                                            border: `1.5px solid ${active ? (cfg.border || '#a5b4fc') : borderCol}`,
                                            background: active ? (cfg.bg || '#eef2ff') : cardBg,
                                            color: active ? (cfg.color || '#6366f1') : textMuted,
                                            borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700,
                                            cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit',
                                        }}>
                                            {label} ({count})
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Cumulative Download + Shortage Report */}
                            {!loading && stockRequests.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                    {showCumulativeBtn && (
                                        <DownloadDropdown
                                            label="📄 Cumulative Download"
                                            isDark={isDark}
                                            onPDF={() => handleDownloadCombinedPDF(stockRequests)}
                                            onExcel={() => handleDownloadCombinedExcel(stockRequests)}
                                        />
                                    )}

                                    {(statusFilter === 'RECEIVED' || statusFilter === 'CLOSED') && stockRequests.length > 0 && (
                                        <button
                                            onClick={() => handleDownloadShortageExcel(stockRequests)}
                                            style={{
                                                background: '#fff7ed',
                                                border: '1px solid #fed7aa',
                                                borderRadius: 7,
                                                padding: '6px 13px',
                                                fontSize: 12,
                                                fontWeight: 700,
                                                color: '#ea580c',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 5,
                                                fontFamily: 'inherit',
                                                transition: 'background .15s',
                                            }}
                                            onMouseEnter={e => e.currentTarget.style.background = '#fed7aa'}
                                            onMouseLeave={e => e.currentTarget.style.background = '#fff7ed'}
                                        >
                                            ⚠️ Shortage Report
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {/* ── Error ── */}
                    {error && (
                        <div style={{ background: '#e4e2fe', border: '1px solid #a5a6fc', borderRadius: 9, padding: '11px 15px', marginBottom: 14, color: '#262fdc', fontSize: 13 }}>{error}</div>
                    )}

                    {/* ── Loading Skeleton ── */}
                    {loading && (
                        <>
                            <style>{`@keyframes shimmer{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
                            {[1, 2, 3].map(i => (
                                <div key={i} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '16px 18px', marginBottom: 12, animation: 'shimmer 1.5s ease-in-out infinite' }}>
                                    <div style={{ height: 14, width: '32%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5, marginBottom: 10 }} />
                                    <div style={{ height: 11, width: '20%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5 }} />
                                </div>
                            ))}
                        </>
                    )}

                    {/* ── Empty State ── */}
                    {!loading && !error && stockRequests.length === 0 && (
                        <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '52px 20px', textAlign: 'center' }}>
                            <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                            <div style={{ fontWeight: 800, fontSize: 15, color: textMain, marginBottom: 5 }}>No stock requests found</div>
                            <div style={{ color: textMuted, fontSize: 13 }}>
                                {searchTerm || statusFilter !== 'ALL' ? 'Try adjusting your search or filters' : selectedOutlet?.value ? 'This outlet has no stock requests for the selected date range' : 'Stock requisitions will appear here once raised'}
                            </div>
                        </div>
                    )}

                    {/* ── Request Cards ── */}
                    {!loading && stockRequests
                        .filter(req => {
                            if (!searchTerm) return true;
                            const search = searchTerm.toLowerCase();
                            return (
                                req.indentNumber?.toLowerCase().includes(search) ||
                                req.outlet?.name?.toLowerCase().includes(search) ||
                                req.outlet?.outletName?.toLowerCase().includes(search) ||
                                req.items?.some(i => i.rawMaterial?.name?.toLowerCase().includes(search))
                            );
                        })
                        .map(req => (
                            <RequestCard key={req._id} req={req} isDark={isDark} onUpdate={handleUpdateClick} />
                        ))}

                    {/* ── Pagination ── */}
                    {!loading && stockRequests.length > 0 && totalPages > 1 && (
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} isDark={isDark} />
                    )}

                    {/* ── Footer Info ── */}
                    {!loading && stockRequests.length > 0 && (
                        <div style={{ textAlign: 'center', color: textMuted, fontSize: 12, marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span>Showing {((currentPage - 1) * 20) + 1} - {Math.min(currentPage * 20, totalRecords)} of {totalRecords} request{totalRecords !== 1 ? 's' : ''}</span>
                            <span style={{ color: borderCol }}>•</span>
                            <span>Page {currentPage} of {totalPages}</span>
                        </div>
                    )}

                </Container>
            </div>
        </React.Fragment>
    );
};

export default StocksRequest;




// New Code For Implementing the new logistic flow
// import React, { useState, useEffect, useRef } from 'react';
// import { useSelector } from 'react-redux';
// import Header from '../../layouts/Header';
// import { Container, Spinner, Form } from 'react-bootstrap';
// import Select from 'react-select';
// import axiosInstance from '../../api/axios';
// import OutletSelect from '../../components/OutletSelect';
// import { useNavigate } from 'react-router-dom';
// import jsPDF from 'jspdf';
// import autoTable from 'jspdf-autotable';
// import XLSX from 'xlsx-js-style';
// import IntendImages from '../../assets/img/indentImg.png';

// /* ─── constants ──────── */
// const STATUS_CONFIG = {
//     PENDING: { label: 'Pending', color: '#d97706', bg: '#fef3c7', border: '#fcd34d' },
//     ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
//     REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
//     IN_PRODUCTION: { label: 'In-Production', color: '#7c3aed', bg: '#ede9fe', border: '#c4b5fd' },
//     READY_TO_PICK: { label: 'Ready-to-Pick', color: '#0891b2', bg: '#cffafe', border: '#67e8f9' },
//     RECEIVED: { label: 'Received', color: '#2563eb', bg: '#dbeafe', border: '#93c5fd' },
//     CLOSED: { label: 'Closed', color: '#374151', bg: '#f3f4f6', border: '#9ca3af' },
// };

// const UPDATABLE_STATUSES = {
//     ACCEPTED: { label: 'Accepted', color: '#059669', bg: '#d1fae5', border: '#6ee7b7' },
//     REJECTED: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
// };

// // ADD THIS ↓
// const STATUS_COUNT_KEY_MAP = {
//     PENDING: 'pending',
//     ACCEPTED: 'accepted',
//     REJECTED: 'rejected',
//     IN_PRODUCTION: 'inProduction',
//     READY_TO_PICK: 'readyToPick',
//     RECEIVED: 'received',
//     CLOSED: 'closed',
// };

// const fmt = (iso) =>
//     new Date(iso).toLocaleString('en-IN', {
//         day: '2-digit', month: 'short', year: 'numeric',
//         hour: '2-digit', minute: '2-digit',
//     });

// const formatDate = (iso) =>
//     new Date(iso).toLocaleDateString('en-IN', {
//         day: '2-digit', month: 'short', year: 'numeric',
//     });

// /* ─── date filter presets ──────────────────────────────── */
// const DATE_FILTER_OPTIONS = [
//     { key: 'TODAY', label: 'Today' },
//     { key: 'YESTERDAY', label: 'Yesterday' },
//     { key: 'LAST_7_DAYS', label: 'Last 7 Days' },
//     { key: 'LAST_30_DAYS', label: 'Last 30 Days' },
//     { key: 'LAST_MONTH', label: 'Last Month' },
//     { key: 'LAST_3_MONTHS', label: 'Last 3 Months' },
//     { key: 'CUSTOM', label: 'Custom Range' },
// ];

// // Formats a Date object as YYYY-MM-DD for the API payload
// const formatPayloadDate = (date) => {
//     if (!date) return '';
//     const y = date.getFullYear();
//     const m = String(date.getMonth() + 1).padStart(2, '0');
//     const d = String(date.getDate()).padStart(2, '0');
//     return `${y}-${m}-${d}`;
// };

// // For populating <input type="date"> values (needs YYYY-MM-DD)
// const formatDateForInput = (date) => {
//     if (!date) return '';
//     const year = date.getFullYear();
//     const month = String(date.getMonth() + 1).padStart(2, '0');
//     const day = String(date.getDate()).padStart(2, '0');
//     return `${year}-${month}-${day}`;
// };

// // Computes { start, end } Date objects for a given preset key
// const getDateRangeForOption = (key) => {
//     const today = new Date();
//     today.setHours(0, 0, 0, 0);
//     const start = new Date(today);
//     const end = new Date(today);

//     switch (key) {
//         case 'TODAY':
//             break; // start = end = today

//         case 'YESTERDAY':
//             start.setDate(start.getDate() - 1);
//             end.setDate(end.getDate() - 1);
//             break;

//         case 'LAST_7_DAYS':
//             start.setDate(start.getDate() - 6); // inclusive of today => 7 days total
//             break;

//         case 'LAST_30_DAYS':
//             start.setDate(start.getDate() - 29); // inclusive of today => 30 days total
//             break;

//         case 'LAST_MONTH': {
//             const firstDayThisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
//             const lastDayPrevMonth = new Date(firstDayThisMonth);
//             lastDayPrevMonth.setDate(lastDayPrevMonth.getDate() - 1);
//             const firstDayPrevMonth = new Date(lastDayPrevMonth.getFullYear(), lastDayPrevMonth.getMonth(), 1);
//             return { start: firstDayPrevMonth, end: lastDayPrevMonth };
//         }

//         case 'LAST_3_MONTHS':
//             start.setMonth(start.getMonth() - 3);
//             start.setDate(start.getDate() + 1); // inclusive of today
//             break;

//         default:
//             break;
//     }
//     return { start, end };
// };

// /* ─── PDF Download (single request) ──────────────────────── */
// const handleDownloadPDF = (request) => {
//     const doc = new jsPDF('p', 'mm', 'a4');
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const primaryColor = [9, 165, 247];
//     const upperStatus = (request.status || 'PENDING').toUpperCase();
//     const showReceivedQty = request.status === 'RECEIVED' || request.status === 'CLOSED';

//     doc.setFillColor(...primaryColor);
//     doc.rect(0, 0, pageWidth, 28, 'F');

//     try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

//     doc.setTextColor(255, 255, 255);
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(15);
//     doc.text('STOCK REQUEST', pageWidth - 14, 13, { align: 'right' });
//     doc.setFontSize(8);
//     doc.setFont('helvetica', 'normal');
//     doc.text(`${request.indentNumber || ''}`, pageWidth - 14, 20, { align: 'right' });
//     doc.setTextColor(40, 40, 40);

//     const startY = 36;
//     const col1Label = 14, col1Value = 38, col2Label = 110, col2Value = 128;
//     doc.setFontSize(8.5);

//     doc.setFont('helvetica', 'normal'); doc.text('Indent :', col1Label, startY);
//     doc.setFont('helvetica', 'bold'); doc.text(request.indentNumber || '-', col1Value, startY);

//     doc.setFont('helvetica', 'normal'); doc.text('Status :', col1Label, startY + 6);
//     doc.setFont('helvetica', 'bold'); doc.text(upperStatus, col1Value, startY + 6);

//     doc.setFont('helvetica', 'normal'); doc.text('Date :', col1Label, startY + 12);
//     doc.setFont('helvetica', 'bold'); doc.text(formatDate(request.createdAt), col1Value, startY + 12);

//     doc.setFont('helvetica', 'normal'); doc.text('Fin. Year :', col1Label, startY + 18);
//     doc.setFont('helvetica', 'bold'); doc.text(request.fin_year || '-', col1Value, startY + 18);

//     doc.setFont('helvetica', 'normal'); doc.text('Outlet :', col2Label, startY);
//     doc.setFont('helvetica', 'bold'); doc.text(request?.outlet?.name || '-', col2Value, startY);

//     doc.setFont('helvetica', 'normal'); doc.text('City :', col2Label, startY + 6);
//     doc.setFont('helvetica', 'bold'); doc.text(request?.outlet?.city || '-', col2Value, startY + 6);

//     doc.setFont('helvetica', 'normal'); doc.text('Address :', col2Label, startY + 12);
//     doc.setFont('helvetica', 'bold');
//     const addressLines = doc.splitTextToSize(request?.outlet?.address || '-', 60);
//     doc.text(addressLines, col2Value, startY + 12);

//     const dividerY = startY + 28;
//     doc.setDrawColor(200, 200, 200);
//     doc.line(14, dividerY, pageWidth - 14, dividerY);

//     const items = request.items || [];
//     let totalRequested = 0, totalReceived = 0;

//     const sortedItems = [...items].sort((a, b) => {
//         console.log("A in line no 2020 :", a);
//         console.log("B in line no 2021 :", b);
//         const mainCompare = (a.rawMaterial?.mainCategoryName || '').localeCompare(b.rawMaterial?.mainCategoryName || '');
//         if (mainCompare !== 0) return mainCompare;
//         return (a.rawMaterial?.subCategoryName || '').localeCompare(b.rawMaterial?.subCategoryName || '');
//     });

//     // Build columns dynamically
//     const head = [['Sr No', 'Material', 'Main Cat', 'Sub Cat', 'Unit', 'Req Qty']];
//     if (showReceivedQty) head[0].push('Rec Qty', 'Balance Qty');

//     const tableRows = sortedItems.map((it, index) => {
//         const rm = it.rawMaterial || {};
//         const requested = Number(it.requestedQuantity || 0);
//         const received = Number(it.totalReceivedQuantity || 0);
//         const balance = requested - received;
//         totalRequested += requested;
//         totalReceived += received;

//         const row = [
//             index + 1,
//             rm.name || '-',
//             rm.mainCategoryName || '-',
//             rm.subCategoryName || '-',
//             rm.unit || '-',
//             requested.toFixed(2),
//         ];
//         if (showReceivedQty) row.push(received.toFixed(2), balance.toFixed(2));
//         return row;
//     });

//     const columnStyles = {
//         0: { halign: 'center', cellWidth: 10 },
//         1: { halign: 'left' },
//         2: { halign: 'center', cellWidth: showReceivedQty ? 24 : 28 },
//         3: { halign: 'center', cellWidth: showReceivedQty ? 24 : 28 },
//         4: { halign: 'center', cellWidth: 20 },
//         5: { halign: 'center', cellWidth: showReceivedQty ? 16 : 18 },
//     };
//     if (showReceivedQty) {
//         columnStyles[6] = { halign: 'center', cellWidth: 16 };
//         columnStyles[7] = { halign: 'center', cellWidth: 18 };
//     }

//     autoTable(doc, {
//         startY: dividerY + 6,
//         head,
//         body: tableRows,
//         theme: 'grid',
//         headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
//         styles: { fontSize: 8, cellPadding: 2.5, font: 'helvetica', valign: 'middle' },
//         alternateRowStyles: { fillColor: [245, 250, 255] },
//         columnStyles,
//     });

//     const finalY = doc.lastAutoTable.finalY + 8;
//     doc.setFontSize(8.5);
//     doc.setFont('helvetica', 'bold');
//     let totalsText = `Total Items: ${items.length}   |   Total Requested: ${totalRequested.toFixed(2)}`;
//     if (showReceivedQty) {
//         totalsText += `   |   Total Received: ${totalReceived.toFixed(2)}   |   Balance: ${(totalRequested - totalReceived).toFixed(2)}`;
//     }
//     doc.text(totalsText, pageWidth - 14, finalY, { align: 'right' });

//     doc.setFont('helvetica', 'normal');
//     doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
//     doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

//     doc.setFontSize(7);
//     doc.setTextColor(100, 100, 100);
//     doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

//     doc.save(`Stock-Request-${request.indentNumber || 'Details'}.pdf`);
// };

// /* ─── Excel style helpers ────────────────────────────────── */
// const PRIMARY = '09A5F7'; // sky blue
// const WHITE = 'FFFFFF';
// const DARK = '1F2937';
// const MUTED = '6B7280';
// const ALT_ROW = 'F0F7FF';
// const TOTAL_BG = 'EEF2FF';
// const INDIGO = '4F46E5';
// const GREEN_BG = 'D1FAE5';
// const GREEN_FG = '059669';
// const RED_FG = 'DC2626';

// const border = (color = 'FFD1D5DB') => ({
//     top: { style: 'thin', color: { rgb: color } },
//     bottom: { style: 'thin', color: { rgb: color } },
//     left: { style: 'thin', color: { rgb: color } },
//     right: { style: 'thin', color: { rgb: color } },
// });

// const headerCell = (v) => ({
//     v, t: 's',
//     s: {
//         font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 10 },
//         fill: { fgColor: { rgb: PRIMARY } },
//         alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
//         border: border('09A5F7'),
//     },
// });

// const infoLabelCell = (v) => ({
//     v, t: 's',
//     s: {
//         font: { name: 'Arial', bold: true, color: { rgb: MUTED }, sz: 9 },
//         fill: { fgColor: { rgb: 'F9FAFB' } },
//         alignment: { horizontal: 'left', vertical: 'center' },
//         border: border(),
//     },
// });

// const infoValueCell = (v) => ({
//     v, t: 's',
//     s: {
//         font: { name: 'Arial', bold: true, color: { rgb: DARK }, sz: 9 },
//         fill: { fgColor: { rgb: 'F9FAFB' } },
//         alignment: { horizontal: 'left', vertical: 'center' },
//         border: border(),
//     },
// });

// const titleCell = (v) => ({
//     v, t: 's',
//     s: {
//         font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 16 },
//         fill: { fgColor: { rgb: PRIMARY } },
//         alignment: { horizontal: 'center', vertical: 'center' },
//     },
// });

// const dataCell = (v, isAlt, opts = {}) => ({
//     v,
//     t: typeof v === 'number' ? 'n' : 's',
//     z: typeof v === 'number' ? '#,##0.00' : undefined,
//     s: {
//         font: { name: 'Arial', color: { rgb: opts.color || DARK }, sz: 10, bold: opts.bold || false },
//         fill: { fgColor: { rgb: opts.bg || (isAlt ? ALT_ROW : WHITE) } },
//         alignment: { horizontal: opts.align || 'left', vertical: 'center', wrapText: true },
//         border: border(),
//     },
// });

// const totalCell = (v, opts = {}) => ({
//     v,
//     t: typeof v === 'number' ? 'n' : 's',
//     z: typeof v === 'number' ? '#,##0.00' : undefined,
//     s: {
//         font: { name: 'Arial', bold: true, color: { rgb: opts.color || INDIGO }, sz: 11 },
//         fill: { fgColor: { rgb: TOTAL_BG } },
//         alignment: { horizontal: opts.align || 'center', vertical: 'center' },
//         border: {
//             top: { style: 'medium', color: { rgb: 'C4B5FD' } },
//             bottom: { style: 'medium', color: { rgb: 'C4B5FD' } },
//             left: { style: 'thin', color: { rgb: 'C4B5FD' } },
//             right: { style: 'thin', color: { rgb: 'C4B5FD' } },
//         },
//     },
// });

// const blankCell = (bg = PRIMARY) => ({
//     v: '', t: 's',
//     s: { fill: { fgColor: { rgb: bg } } },
// });

// /* ─── Apply cells to worksheet ───────────────────────────── */
// const setCells = (ws, rowStart, colStart, matrix) => {
//     matrix.forEach((row, ri) => {
//         row.forEach((cell, ci) => {
//             if (cell === null) return;
//             const addr = XLSX.utils.encode_cell({ r: rowStart + ri, c: colStart + ci });
//             ws[addr] = cell;
//         });
//     });
// };

// const setMerge = (ws, r1, c1, r2, c2) => {
//     if (!ws['!merges']) ws['!merges'] = [];
//     ws['!merges'].push({ s: { r: r1, c: c1 }, e: { r: r2, c: c2 } });
// };

// /* ─── Shortage Report Excel (Received < Requested, grouped by outlet) ── */
// const handleDownloadShortageExcel = (allRequests) => {
//     const outletMap = {};

//     allRequests.forEach(req => {
//         if (req.status !== 'RECEIVED' && req.status !== 'CLOSED') return;
//         const outletId = req?.outlet?._id || req?.outlet || 'unknown';
//         const outletName = req?.outlet?.name || 'Unknown Outlet';
//         const outletCity = req?.outlet?.city || '—';

//         if (!outletMap[outletId]) {
//             outletMap[outletId] = { outletName, outletCity, indents: [] };
//         }

//         const shortageItems = (req.items || [])
//             .filter(item => Number(item.totalReceivedQuantity || 0) < Number(item.requestedQuantity || 0))
//             .sort((a, b) => {
//                 const c = (a.rawMaterial?.mainCategory?.name || '').localeCompare(b.rawMaterial?.mainCategory?.name || '');
//                 return c !== 0 ? c : (a.rawMaterial?.subCategory?.name || '').localeCompare(b.rawMaterial?.subCategory?.name || '');
//             });

//         if (shortageItems.length > 0) {
//             outletMap[outletId].indents.push({
//                 indentNumber: req.indentNumber || '—',
//                 createdAt: req.createdAt,
//                 status: req.status,
//                 items: shortageItems,
//             });
//         }
//     });

//     const outlets = Object.values(outletMap)
//         .filter(o => o.indents.length > 0)
//         .map(o => ({
//             ...o,
//             indents: o.indents.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
//         }));

//     if (outlets.length === 0) {
//         alert('No shortage items found. All requested quantities were fulfilled!');
//         return;
//     }

//     const LAVENDER_TITLE = '7C3AED';
//     const LAVENDER_LIGHT = 'F5F3FF';
//     const LAVENDER_ALT = 'EDE9FE';
//     const LAVENDER_BORDER = 'C4B5FD';
//     const LAVENDER_TEXT = '6D28D9';
//     const LAVENDER_HEADER = '8B5CF6';

//     const INDENT_BG = 'F1F5F9';
//     const INDENT_BORDER = 'CBD5E1';
//     const INDENT_TEXT = '1E40AF';

//     const wb = XLSX.utils.book_new();

//     const lavHeaderCell = (v) => ({
//         v, t: 's',
//         s: {
//             font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 10 },
//             fill: { fgColor: { rgb: LAVENDER_HEADER } },
//             alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
//             border: {
//                 top: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 bottom: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 left: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 right: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//             },
//         },
//     });

//     const lavTitleCell = (v) => ({
//         v, t: 's',
//         s: {
//             font: { name: 'Arial', bold: true, color: { rgb: WHITE }, sz: 16 },
//             fill: { fgColor: { rgb: LAVENDER_TITLE } },
//             alignment: { horizontal: 'center', vertical: 'center' },
//         },
//     });

//     const lavBlankCell = () => ({
//         v: '', t: 's',
//         s: { fill: { fgColor: { rgb: LAVENDER_TITLE } } },
//     });

//     const lavSubtitleCell = (v) => ({
//         v, t: 's',
//         s: {
//             font: { name: 'Arial', color: { rgb: WHITE }, sz: 9 },
//             fill: { fgColor: { rgb: LAVENDER_TITLE } },
//             alignment: { horizontal: 'center', vertical: 'center' },
//         },
//     });

//     const indentGroupCell = (v) => ({
//         v, t: 's',
//         s: {
//             font: { name: 'Arial', bold: true, color: { rgb: INDENT_TEXT }, sz: 10 },
//             fill: { fgColor: { rgb: INDENT_BG } },
//             alignment: { horizontal: 'left', vertical: 'center' },
//             border: {
//                 top: { style: 'medium', color: { rgb: INDENT_BORDER } },
//                 bottom: { style: 'medium', color: { rgb: INDENT_BORDER } },
//                 left: { style: 'medium', color: { rgb: INDENT_BORDER } },
//                 right: { style: 'thin', color: { rgb: INDENT_BORDER } },
//             },
//         },
//     });

//     const indentGroupBlank = () => ({
//         v: '', t: 's',
//         s: {
//             fill: { fgColor: { rgb: INDENT_BG } },
//             border: {
//                 top: { style: 'medium', color: { rgb: INDENT_BORDER } },
//                 bottom: { style: 'medium', color: { rgb: INDENT_BORDER } },
//                 left: { style: 'thin', color: { rgb: INDENT_BORDER } },
//                 right: { style: 'thin', color: { rgb: INDENT_BORDER } },
//             },
//         },
//     });

//     const shortageCell = (v, isAlt) => ({
//         v, t: 'n', z: '#,##0.00',
//         s: {
//             font: { name: 'Arial', bold: true, color: { rgb: LAVENDER_TEXT }, sz: 10 },
//             fill: { fgColor: { rgb: isAlt ? LAVENDER_ALT : LAVENDER_LIGHT } },
//             alignment: { horizontal: 'center', vertical: 'center' },
//             border: {
//                 top: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 bottom: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 left: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//                 right: { style: 'thin', color: { rgb: LAVENDER_BORDER } },
//             },
//         },
//     });

//     const summaryWs = {};
//     const summaryLastCol = 5;

//     summaryWs[XLSX.utils.encode_cell({ r: 0, c: 0 })] = lavTitleCell('SHORTAGE REPORT — SUMMARY');
//     for (let c = 1; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 0, c })] = lavBlankCell();
//     setMerge(summaryWs, 0, 0, 0, summaryLastCol);

//     summaryWs[XLSX.utils.encode_cell({ r: 1, c: 0 })] = lavSubtitleCell(
//         `Generated ${new Date().toLocaleString('en-IN')} · ${allRequests.length} indent(s) analysed`
//     );
//     for (let c = 1; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 1, c })] = lavBlankCell();
//     setMerge(summaryWs, 1, 0, 1, summaryLastCol);

//     for (let c = 0; c <= summaryLastCol; c++) summaryWs[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's' };

//     ['Outlet', 'City', 'Indents with Shortages', 'Total Shortage Items', 'Total Shortage Qty', 'Sheet']
//         .forEach((h, c) => { summaryWs[XLSX.utils.encode_cell({ r: 3, c })] = lavHeaderCell(h); });

//     outlets.forEach((o, idx) => {
//         const r = 4 + idx;
//         const isAlt = idx % 2 === 1;
//         const rowBg = isAlt ? LAVENDER_ALT : LAVENDER_LIGHT;
//         const totalShortageItems = o.indents.reduce((s, i) => s + i.items.length, 0);
//         const totalShortageQty = o.indents.reduce((s, i) =>
//             s + i.items.reduce((ss, item) =>
//                 ss + (Number(item.requestedQuantity || 0) - Number(item.totalReceivedQuantity || 0)), 0), 0);
//         const sheetName = o.outletName.substring(0, 28).replace(/[\\/:*?[\]]/g, '_');

//         [
//             dataCell(o.outletName, isAlt, { align: 'left', bold: true, bg: rowBg }),
//             dataCell(o.outletCity, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(o.indents.length, isAlt, { align: 'center', bold: true, color: LAVENDER_TEXT, bg: rowBg }),
//             dataCell(totalShortageItems, isAlt, { align: 'center', bold: true, color: LAVENDER_TEXT, bg: rowBg }),
//             shortageCell(totalShortageQty, isAlt),
//             dataCell(sheetName, isAlt, { align: 'center', color: '2563EB', bg: rowBg }),
//         ].forEach((cell, c) => { summaryWs[XLSX.utils.encode_cell({ r, c })] = cell; });
//     });

//     const summaryFootR = 4 + outlets.length + 1;
//     summaryWs[XLSX.utils.encode_cell({ r: summaryFootR, c: 0 })] = {
//         v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
//         s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
//     };
//     setMerge(summaryWs, summaryFootR, 0, summaryFootR, summaryLastCol);
//     summaryWs['!cols'] = [{ wch: 30 }, { wch: 18 }, { wch: 22 }, { wch: 20 }, { wch: 20 }, { wch: 28 }];
//     summaryWs['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }];
//     summaryWs['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: summaryFootR, c: summaryLastCol } });
//     XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

//     const lastColIdx = 8;

//     outlets.forEach(o => {
//         const sheetName = o.outletName.substring(0, 28).replace(/[\\/:*?[\]]/g, '_');
//         const ws = {};
//         let currentRow = 0;

//         ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = lavTitleCell('SHORTAGE REPORT');
//         for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavBlankCell();
//         setMerge(ws, currentRow, 0, currentRow, lastColIdx);
//         currentRow++;

//         ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = lavSubtitleCell(`${o.outletName} · ${o.outletCity}`);
//         for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavBlankCell();
//         setMerge(ws, currentRow, 0, currentRow, lastColIdx);
//         currentRow++;

//         for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = { v: '', t: 's' };
//         currentRow++;

//         ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty', 'Rec Qty', 'Shortage']
//             .forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = lavHeaderCell(h); });
//         currentRow++;

//         o.indents.forEach((indent) => {
//             ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = indentGroupCell(
//                 `  📋  ${indent.indentNumber}     |     ${formatDate(indent.createdAt)}     |     Status: ${indent.status}     |     ${indent.items.length} shortage item${indent.items.length !== 1 ? 's' : ''}`
//             );
//             for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = indentGroupBlank();
//             setMerge(ws, currentRow, 0, currentRow, lastColIdx);
//             currentRow++;

//             let indentTotalReq = 0, indentTotalRec = 0;
//             indent.items.forEach((item, idx) => {
//                 const rm = item.rawMaterial || {};
//                 const requested = Number(item.requestedQuantity || 0);
//                 const received = Number(item.totalReceivedQuantity || 0);
//                 const shortage = requested - received;
//                 indentTotalReq += requested;
//                 indentTotalRec += received;

//                 const isAlt = idx % 2 === 1;
//                 const rowBg = isAlt ? LAVENDER_ALT : LAVENDER_LIGHT;

//                 [
//                     dataCell(idx + 1, isAlt, { align: 'center', bg: rowBg }),
//                     dataCell(rm.name || '—', isAlt, { align: 'left', bold: true, bg: rowBg }),
//                     dataCell(rm.mainCategoryName || '—', isAlt, { align: 'center', bg: rowBg }),
//                     dataCell(rm.subCategoryName || '—', isAlt, { align: 'center', bg: rowBg }),
//                     dataCell(rm.hsn || '—', isAlt, { align: 'center', bg: rowBg }),
//                     dataCell(rm.unit || '—', isAlt, { align: 'center', bg: rowBg }),
//                     dataCell(requested, isAlt, { align: 'center', bold: true, color: INDIGO, bg: rowBg }),
//                     dataCell(received, isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }),
//                     shortageCell(shortage, isAlt),
//                 ].forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = cell; });
//                 currentRow++;
//             });

//             [
//                 totalCell(''), totalCell(''), totalCell(''), totalCell(''), totalCell(''),
//                 totalCell('SUBTOTAL', { align: 'right', color: DARK }),
//                 totalCell(indentTotalReq, { align: 'center' }),
//                 totalCell(indentTotalRec, { align: 'center', color: GREEN_FG }),
//                 totalCell(indentTotalReq - indentTotalRec, { align: 'center', color: LAVENDER_TEXT }),
//             ].forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: currentRow, c })] = cell; });
//             currentRow++;

//             for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: currentRow, c })] = { v: '', t: 's' };
//             currentRow++;
//         });

//         ws[XLSX.utils.encode_cell({ r: currentRow, c: 0 })] = {
//             v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
//             s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
//         };
//         setMerge(ws, currentRow, 0, currentRow, lastColIdx);

//         ws['!cols'] = [
//             { wch: 7 },
//             { wch: 28 },
//             { wch: 18 },
//             { wch: 18 },
//             { wch: 12 },
//             { wch: 10 },
//             { wch: 13 },
//             { wch: 13 },
//             { wch: 14 },
//         ];
//         ws['!rows'] = [{ hpt: 30 }, { hpt: 18 }, { hpt: 6 }, { hpt: 22 }];
//         ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: currentRow, c: lastColIdx } });

//         XLSX.utils.book_append_sheet(wb, ws, sheetName);
//     });

//     XLSX.writeFile(wb, `Shortage-Report-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
// };

// /* ─── Excel Download (single request) ──────────────────────── */
// const handleDownloadExcel = (request) => {
//     const items = request.items || [];
//     const showReceivedQty = request.status === 'RECEIVED' || request.status === 'CLOSED';
//     const totalCols = showReceivedQty ? 9 : 7;
//     const lastColIdx = totalCols - 1;

//     const sortedItems = [...items].sort((a, b) => {
//         const mc = (a.rawMaterial?.mainCategory?.name || '').localeCompare(b.rawMaterial?.mainCategory?.name || '');
//         return mc !== 0 ? mc : (a.rawMaterial?.subCategory?.name || '').localeCompare(b.rawMaterial?.subCategory?.name || '');
//     });

//     const ws = {};
//     ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 999, c: lastColIdx } });

//     ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = titleCell('STOCK REQUEST');
//     for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 0, c })] = blankCell();
//     setMerge(ws, 0, 0, 0, lastColIdx);

//     ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
//         v: request.indentNumber || '', t: 's',
//         s: { font: { name: 'Arial', bold: false, color: { rgb: WHITE }, sz: 9 }, fill: { fgColor: { rgb: PRIMARY } }, alignment: { horizontal: 'center', vertical: 'center' } },
//     };
//     for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 1, c })] = blankCell();
//     setMerge(ws, 1, 0, 1, lastColIdx);

//     for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'FFFFFF' } } } };

//     const half = Math.floor(lastColIdx / 2);
//     const infoLeft = [
//         ['Indent Number', request.indentNumber || '-'],
//         ['Status', request.status || '-'],
//         ['Date', formatDate(request.createdAt)],
//         ['Financial Year', request.fin_year || '-'],
//     ];
//     const infoRight = [
//         ['Outlet', request?.outlet?.name || '-'],
//         ['City', request?.outlet?.city || '-'],
//         ['Address', request?.outlet?.address || '-'],
//         ['', ''],
//     ];

//     infoLeft.forEach(([label, value], i) => {
//         const r = 3 + i;
//         ws[XLSX.utils.encode_cell({ r, c: 0 })] = infoLabelCell(label);
//         ws[XLSX.utils.encode_cell({ r, c: 1 })] = infoValueCell(value);
//         for (let c = 2; c <= half; c++) ws[XLSX.utils.encode_cell({ r, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'F9FAFB' } }, border: border() } };
//         setMerge(ws, r, 1, r, half);
//     });
//     infoRight.forEach(([label, value], i) => {
//         const r = 3 + i;
//         ws[XLSX.utils.encode_cell({ r, c: half + 1 })] = infoLabelCell(label);
//         ws[XLSX.utils.encode_cell({ r, c: half + 2 })] = infoValueCell(value);
//         for (let c = half + 3; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'F9FAFB' } }, border: border() } };
//         setMerge(ws, r, half + 2, r, lastColIdx);
//     });

//     for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 7, c })] = { v: '', t: 's', s: { fill: { fgColor: { rgb: 'E5E7EB' } } } };

//     const headers = ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty'];
//     if (showReceivedQty) headers.push('Rec Qty', 'Balance Qty');
//     headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: 8, c })] = headerCell(h); });

//     let totalRequested = 0, totalReceived = 0;
//     sortedItems.forEach((it, idx) => {
//         const rm = it.rawMaterial || {};
//         const requested = Number(it.requestedQuantity || 0);
//         const received = Number(it.totalReceivedQuantity || 0);
//         const balance = requested - received;
//         totalRequested += requested;
//         totalReceived += received;
//         const r = 9 + idx;
//         const isAlt = idx % 2 === 1;

//         const rowCells = [
//             dataCell(idx + 1, isAlt, { align: 'center' }),
//             dataCell(rm.name || '-', isAlt),
//             dataCell(rm.mainCategoryName || '-', isAlt, { align: 'center' }),
//             dataCell(rm.subCategoryName || '-', isAlt, { align: 'center' }),
//             dataCell(rm.hsn || '-', isAlt, { align: 'center' }),
//             dataCell(rm.unit || '-', isAlt, { align: 'center' }),
//             dataCell(requested, isAlt, { align: 'center', bold: true, color: INDIGO }),
//         ];
//         if (showReceivedQty) {
//             rowCells.push(dataCell(received, isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }));
//             rowCells.push(dataCell(balance, isAlt, { align: 'center', bold: true, color: balance > 0 ? RED_FG : GREEN_FG }));
//         }
//         rowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r, c })] = cell; });
//     });

//     const totalsR = 9 + sortedItems.length;
//     const totalRowCells = [
//         totalCell('', { align: 'center' }),
//         totalCell('', { align: 'center' }),
//         totalCell('', { align: 'center' }),
//         totalCell('', { align: 'center' }),
//         totalCell('', { align: 'center' }),
//         totalCell('TOTAL', { align: 'right', color: DARK }),
//         totalCell(totalRequested, { align: 'center' }),
//     ];
//     if (showReceivedQty) {
//         totalRowCells.push(totalCell(totalReceived, { align: 'center', color: GREEN_FG }));
//         totalRowCells.push(totalCell(totalRequested - totalReceived, { align: 'center', color: RED_FG }));
//     }
//     totalRowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: totalsR, c })] = cell; });

//     const footR = totalsR + 2;
//     ws[XLSX.utils.encode_cell({ r: footR, c: 0 })] = {
//         v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
//         s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
//     };
//     setMerge(ws, footR, 0, footR, lastColIdx);

//     ws['!cols'] = [
//         { wch: 7 },
//         { wch: 30 },
//         { wch: 18 },
//         { wch: 18 },
//         { wch: 12 },
//         { wch: 10 },
//         { wch: 13 },
//         ...(showReceivedQty ? [{ wch: 13 }, { wch: 13 }] : []),
//     ];

//     ws['!rows'] = [
//         { hpt: 30 },
//         { hpt: 18 },
//         { hpt: 6 },
//         { hpt: 18 }, { hpt: 18 }, { hpt: 18 }, { hpt: 18 },
//         { hpt: 6 },
//         { hpt: 22 },
//     ];

//     ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: footR, c: lastColIdx } });

//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Stock Request');
//     XLSX.writeFile(wb, `Stock-Request-${request.indentNumber || 'Details'}.xlsx`);
// };

// /* ─── Combined PDF (aggregated) ──────────────────────────── */
// const handleDownloadCombinedPDF = (allRequests) => {
//     const doc = new jsPDF('p', 'mm', 'a4');
//     const pageWidth = doc.internal.pageSize.getWidth();
//     const primaryColor = [9, 165, 247];

//     doc.setFillColor(...primaryColor);
//     doc.rect(0, 0, pageWidth, 28, 'F');

//     try { doc.addImage(IntendImages, 'PNG', 10, 4, 50, 20); } catch (e) { }

//     doc.setTextColor(255, 255, 255);
//     doc.setFont('helvetica', 'bold');
//     doc.setFontSize(14);
//     doc.text('Cumulative Stock Request', (pageWidth / 2) + 20, 16, { align: 'center' });
//     doc.setTextColor(40, 40, 40);

//     const materialMap = {};
//     allRequests.forEach(req => {
//         (req.items || []).forEach(item => {
//             const rm = item.rawMaterial || {};
//             const id = rm._id || rm.name || 'unknown';
//             if (!materialMap[id]) {
//                 materialMap[id] = {
//                     name: rm.name || '—',
//                     mainCat: rm.mainCategory?.name || '—',
//                     subCat: rm.subCategory?.name || '—',
//                     unit: rm.unit || '—',
//                     totalRequested: 0,
//                     totalReceived: 0,
//                 };
//             }
//             materialMap[id].totalRequested += Number(item.requestedQuantity || 0);
//             materialMap[id].totalReceived += Number(item.totalReceivedQuantity || 0);
//         });
//     });

//     const hasReceivedData = allRequests.some(r => r.status === 'RECEIVED' || r.status === 'CLOSED');

//     const rows = Object.values(materialMap)
//         .sort((a, b) => {
//             const c = a.mainCat.localeCompare(b.mainCat);
//             return c !== 0 ? c : a.subCat.localeCompare(b.subCat);
//         })
//         .map((m, idx) => {
//             const balance = m.totalRequested - m.totalReceived;
//             const row = [
//                 idx + 1,
//                 m.name,
//                 `${m.mainCat} > ${m.subCat}`,
//                 Number(m.totalRequested).toFixed(2),
//                 m.unit,
//             ];
//             if (hasReceivedData) row.push(Number(m.totalReceived).toFixed(2), balance.toFixed(2));
//             return row;
//         });

//     const head = [['Sr No', 'Material', 'Category', 'Req. Qty', 'Unit']];
//     if (hasReceivedData) head[0].push('Rec. Qty', 'Balance Qty');

//     const columnStyles = {
//         0: { halign: 'center', cellWidth: 10 },
//         1: { halign: 'left', cellWidth: hasReceivedData ? 48 : 60 },
//         2: { halign: 'left', cellWidth: hasReceivedData ? 48 : 60 },
//         3: { halign: 'center', cellWidth: hasReceivedData ? 20 : 25 },
//         4: { halign: 'center', cellWidth: hasReceivedData ? 18 : 25 },
//     };
//     if (hasReceivedData) {
//         columnStyles[5] = { halign: 'center', cellWidth: 20 };
//         columnStyles[6] = { halign: 'center', cellWidth: 20 };
//     }

//     autoTable(doc, {
//         startY: 34,
//         head,
//         body: rows,
//         theme: 'grid',
//         headStyles: { fillColor: primaryColor, textColor: 255, fontStyle: 'bold', halign: 'center' },
//         styles: { fontSize: 8, cellPadding: 2, font: 'helvetica' },
//         alternateRowStyles: { fillColor: [245, 250, 255] },
//         columnStyles,
//     });

//     const finalY = doc.lastAutoTable.finalY + 8;
//     doc.setFontSize(9);
//     doc.setFont('helvetica', 'bold');
//     doc.text(`Materials: ${rows.length}`, pageWidth - 14, finalY, { align: 'right' });
//     doc.setFont('helvetica', 'normal');
//     doc.text('Authorized Signature', pageWidth - 60, finalY + 18);
//     doc.line(pageWidth - 60, finalY + 20, pageWidth - 14, finalY + 20);

//     doc.setFontSize(7);
//     doc.setTextColor(100, 100, 100);
//     doc.text(`Generated on ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.getHeight() - 10);

//     doc.save(`Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.pdf`);
// };

// /* ─── Combined Excel (aggregated) ──────────────────────────── */
// const handleDownloadCombinedExcel = (allRequests) => {
//     const materialMap = {};
//     allRequests.forEach(req => {
//         (req.items || []).forEach(item => {
//             const rm = item.rawMaterial || {};
//             const id = rm._id || rm.name || 'unknown';
//             if (!materialMap[id]) {
//                 materialMap[id] = {
//                     name: rm.name || '—',
//                     mainCat: rm.mainCategoryName || '—',
//                     subCat: rm.subCategoryName || '—',
//                     unit: rm.unit || '—',
//                     hsn: rm.hsn || '—',
//                     totalRequested: 0,
//                     totalReceived: 0,
//                 };
//             }
//             materialMap[id].totalRequested += Number(item.requestedQuantity || 0);
//             materialMap[id].totalReceived += Number(item.totalReceivedQuantity || 0);
//         });
//     });

//     const hasReceivedData = allRequests.some(r => r.status === 'RECEIVED' || r.status === 'CLOSED');
//     const totalCols = hasReceivedData ? 9 : 7;
//     const lastColIdx = totalCols - 1;

//     const sorted = Object.values(materialMap).sort((a, b) => {
//         const c = a.mainCat.localeCompare(b.mainCat);
//         return c !== 0 ? c : a.subCat.localeCompare(b.subCat);
//     });

//     const ws = {};
//     ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 999, c: lastColIdx } });

//     ws[XLSX.utils.encode_cell({ r: 0, c: 0 })] = titleCell('CUMULATIVE STOCK REQUEST');
//     for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 0, c })] = blankCell();
//     setMerge(ws, 0, 0, 0, lastColIdx);

//     ws[XLSX.utils.encode_cell({ r: 1, c: 0 })] = {
//         v: `${allRequests.length} Request${allRequests.length !== 1 ? 's' : ''} · Generated ${new Date().toLocaleString('en-IN')}`,
//         t: 's',
//         s: { font: { name: 'Arial', color: { rgb: WHITE }, sz: 9 }, fill: { fgColor: { rgb: PRIMARY } }, alignment: { horizontal: 'center', vertical: 'center' } },
//     };
//     for (let c = 1; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 1, c })] = blankCell();
//     setMerge(ws, 1, 0, 1, lastColIdx);

//     for (let c = 0; c <= lastColIdx; c++) ws[XLSX.utils.encode_cell({ r: 2, c })] = { v: '', t: 's' };

//     const headers = ['Sr No', 'Material', 'Main Category', 'Sub Category', 'HSN', 'Unit', 'Req Qty'];
//     if (hasReceivedData) headers.push('Rec Qty', 'Balance Qty');
//     headers.forEach((h, c) => { ws[XLSX.utils.encode_cell({ r: 3, c })] = headerCell(h); });

//     let totalRequested = 0, totalReceived = 0;
//     const ORANGE = 'EA580C';

//     sorted.forEach((m, idx) => {
//         const balance = m.totalRequested - m.totalReceived;
//         const isShortage = hasReceivedData && m.totalReceived < m.totalRequested;
//         totalRequested += m.totalRequested;
//         totalReceived += m.totalReceived;
//         const r = 4 + idx;
//         const isAlt = idx % 2 === 1;

//         const rowBg = isShortage ? (isAlt ? 'FFF3E0' : 'FFF7ED') : undefined;
//         const shortageIndicator = isShortage ? '⚠ ' : '';

//         const rowCells = [
//             dataCell(idx + 1, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(shortageIndicator + m.name, isAlt, { bold: isShortage, color: isShortage ? ORANGE : DARK, bg: rowBg }),
//             dataCell(m.mainCat, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(m.subCat, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(m.hsn, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(m.unit, isAlt, { align: 'center', bg: rowBg }),
//             dataCell(m.totalRequested, isAlt, { align: 'center', bold: true, color: INDIGO, bg: rowBg }),
//         ];
//         if (hasReceivedData) {
//             rowCells.push(dataCell(m.totalReceived, isAlt, { align: 'center', bold: true, color: GREEN_FG, bg: isAlt ? 'FFECFDF5' : GREEN_BG }));
//             rowCells.push(
//                 isShortage
//                     ? {
//                         v: balance, t: 'n', z: '#,##0.00',
//                         s: {
//                             font: { name: 'Arial', bold: true, color: { rgb: ORANGE }, sz: 10 },
//                             fill: { fgColor: { rgb: isAlt ? 'FFE0B2' : 'FFF3E0' } },
//                             alignment: { horizontal: 'center', vertical: 'center' },
//                             border: {
//                                 top: { style: 'thin', color: { rgb: 'FED7AA' } },
//                                 bottom: { style: 'thin', color: { rgb: 'FED7AA' } },
//                                 left: { style: 'thin', color: { rgb: 'FED7AA' } },
//                                 right: { style: 'thin', color: { rgb: 'FED7AA' } },
//                             },
//                         },
//                     }
//                     : dataCell(balance, isAlt, { align: 'center', bold: true, color: balance > 0 ? RED_FG : GREEN_FG })
//             );
//         }
//         rowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r, c })] = cell; });
//     });

//     const totalsR = 4 + sorted.length;
//     const totalRowCells = [
//         totalCell(''), totalCell(''), totalCell(''), totalCell(''), totalCell(''),
//         totalCell('TOTAL', { align: 'right', color: DARK }),
//         totalCell(totalRequested, { align: 'center' }),
//     ];
//     if (hasReceivedData) {
//         totalRowCells.push(totalCell(totalReceived, { align: 'center', color: GREEN_FG }));
//         totalRowCells.push(totalCell(totalRequested - totalReceived, { align: 'center', color: RED_FG }));
//     }
//     totalRowCells.forEach((cell, c) => { ws[XLSX.utils.encode_cell({ r: totalsR, c })] = cell; });

//     const footR = totalsR + 2;
//     ws[XLSX.utils.encode_cell({ r: footR, c: 0 })] = {
//         v: `Generated on ${new Date().toLocaleString('en-IN')}`, t: 's',
//         s: { font: { name: 'Arial', italic: true, color: { rgb: MUTED }, sz: 8 }, alignment: { horizontal: 'left' } },
//     };
//     setMerge(ws, footR, 0, footR, lastColIdx);

//     ws['!cols'] = [
//         { wch: 7 },
//         { wch: 32 },
//         { wch: 20 },
//         { wch: 20 },
//         { wch: 12 },
//         { wch: 10 },
//         { wch: 13 },
//         ...(hasReceivedData ? [{ wch: 13 }, { wch: 13 }] : []),
//     ];

//     ws['!rows'] = [
//         { hpt: 30 },
//         { hpt: 18 },
//         { hpt: 6 },
//         { hpt: 22 },
//     ];

//     ws['!ref'] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: footR, c: lastColIdx } });

//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Cumulative');
//     XLSX.writeFile(wb, `Cumulative-Stock-Request-${new Date().toLocaleDateString('en-IN').replace(/\//g, '-')}.xlsx`);
// };

// /* ─── Download Dropdown ─────────────────────────────────── */
// const DownloadDropdown = ({ onPDF, onExcel, label = '📄 Download', isDark }) => {
//     const [open, setOpen] = useState(false);
//     const ref = useRef(null);

//     useEffect(() => {
//         const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
//         document.addEventListener('mousedown', handler);
//         return () => document.removeEventListener('mousedown', handler);
//     }, []);

//     return (
//         <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
//             <button
//                 onClick={() => setOpen(o => !o)}
//                 style={{
//                     background: '#fef3c7',
//                     border: '1px solid #fcd34d',
//                     borderRadius: 7,
//                     padding: '6px 13px',
//                     fontSize: 12,
//                     fontWeight: 700,
//                     color: '#d97706',
//                     cursor: 'pointer',
//                     whiteSpace: 'nowrap',
//                     display: 'flex',
//                     alignItems: 'center',
//                     gap: 5,
//                     fontFamily: 'inherit',
//                     transition: 'background .15s',
//                 }}
//                 onMouseEnter={e => e.currentTarget.style.background = '#fde68a'}
//                 onMouseLeave={e => e.currentTarget.style.background = '#fef3c7'}
//             >
//                 {label}
//                 <span style={{
//                     fontSize: 9,
//                     display: 'inline-block',
//                     transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
//                     transition: 'transform .2s',
//                 }}>▼</span>
//             </button>

//             {open && (
//                 <div style={{
//                     position: 'absolute',
//                     right: 0,
//                     top: 'calc(100% + 4px)',
//                     background: isDark ? '#1e293b' : '#fff',
//                     border: `1px solid ${isDark ? '#2d3748' : '#e5e7eb'}`,
//                     borderRadius: 8,
//                     boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
//                     minWidth: 150,
//                     zIndex: 200,
//                     overflow: 'hidden',
//                 }}>
//                     <button
//                         onClick={() => { onPDF(); setOpen(false); }}
//                         style={{
//                             display: 'flex', alignItems: 'center', gap: 8,
//                             width: '100%', background: 'none', border: 'none',
//                             padding: '9px 14px', fontSize: 13, fontWeight: 600,
//                             color: isDark ? '#f3f4f6' : '#374151',
//                             cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
//                             borderBottom: `1px solid ${isDark ? '#2d3748' : '#f3f4f6'}`,
//                             transition: 'background .12s',
//                         }}
//                         onMouseEnter={e => e.currentTarget.style.background = isDark ? '#252d3d' : '#fef3c7'}
//                         onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
//                     >
//                         <span>📄</span> Download PDF
//                     </button>
//                     <button
//                         onClick={() => { onExcel(); setOpen(false); }}
//                         style={{
//                             display: 'flex', alignItems: 'center', gap: 8,
//                             width: '100%', background: 'none', border: 'none',
//                             padding: '9px 14px', fontSize: 13, fontWeight: 600,
//                             color: isDark ? '#f3f4f6' : '#374151',
//                             cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
//                             transition: 'background .12s',
//                         }}
//                         onMouseEnter={e => e.currentTarget.style.background = isDark ? '#252d3d' : '#d1fae5'}
//                         onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
//                     >
//                         <span>📊</span> Download Excel
//                     </button>
//                 </div>
//             )}
//         </div>
//     );
// };

// /* ─── Date Range Dropdown ─────────────────────────────────── */
// const DateRangeDropdown = ({
//     dateFilterKey, startDate, endDate,
//     onPresetSelect, onCustomDateChange, isDark,
// }) => {
//     const [open, setOpen] = useState(false);
//     const ref = useRef(null);

//     useEffect(() => {
//         const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
//         document.addEventListener('mousedown', handler);
//         return () => document.removeEventListener('mousedown', handler);
//     }, []);

//     const borderCol = isDark ? '#2d3748' : '#e5e7eb';
//     const textMain = isDark ? '#f3f4f6' : '#111827';
//     const textMuted = isDark ? '#9ca3af' : '#6b7280';
//     const cardBg = isDark ? '#1e293b' : '#fff';

//     const selectedLabel = DATE_FILTER_OPTIONS.find(o => o.key === dateFilterKey)?.label || 'Select Range';

//     return (
//         <div ref={ref} style={{ position: 'relative' }}>
//             <Form.Label style={{ color: textMuted, marginBottom: 6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
//                 <i className="bi bi-calendar-range me-2"></i>Date Range
//             </Form.Label>
//             <button
//                 type="button"
//                 onClick={() => setOpen(o => !o)}
//                 style={{
//                     width: '100%', textAlign: 'left',
//                     background: isDark ? '#252d3d' : '#f9fafb',
//                     border: `1px solid ${borderCol}`, borderRadius: 8,
//                     padding: '8px 12px', fontSize: 13, color: textMain,
//                     fontFamily: 'inherit', cursor: 'pointer',
//                     display: 'flex', justifyContent: 'space-between', alignItems: 'center',
//                 }}
//             >
//                 <span>
//                     {selectedLabel}
//                     {dateFilterKey === 'CUSTOM' && startDate && endDate && (
//                         <span style={{ color: textMuted, marginLeft: 6, fontSize: 11 }}>
//                             ({formatPayloadDate(startDate)} – {formatPayloadDate(endDate)})
//                         </span>
//                     )}
//                 </span>
//                 <span style={{ fontSize: 9, transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
//             </button>

//             {open && (
//                 <div style={{
//                     position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
//                     background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 8,
//                     boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300, overflow: 'hidden',
//                     minWidth: 260,
//                 }}>
//                     {DATE_FILTER_OPTIONS.filter(o => o.key !== 'CUSTOM').map(opt => (
//                         <button
//                             type="button"
//                             key={opt.key}
//                             onClick={() => { onPresetSelect(opt.key); setOpen(false); }}
//                             style={{
//                                 display: 'block', width: '100%', textAlign: 'left',
//                                 background: dateFilterKey === opt.key ? (isDark ? '#252d3d' : '#eef2ff') : 'transparent',
//                                 border: 'none', padding: '9px 14px', fontSize: 13, fontWeight: 600,
//                                 color: dateFilterKey === opt.key ? '#6366f1' : textMain,
//                                 cursor: 'pointer', fontFamily: 'inherit',
//                                 borderBottom: `1px solid ${isDark ? '#2d3748' : '#f3f4f6'}`,
//                             }}
//                         >
//                             {opt.label}
//                         </button>
//                     ))}

//                     <div style={{ padding: '10px 14px', background: isDark ? '#1a2132' : '#fafafa' }}>
//                         <div style={{ fontSize: 12, fontWeight: 700, color: textMuted, marginBottom: 8 }}>Custom Range</div>
//                         <div style={{ display: 'flex', gap: 8 }}>
//                             <input
//                                 type="date"
//                                 value={formatDateForInput(startDate)}
//                                 max={formatDateForInput(endDate) || formatDateForInput(new Date())}
//                                 onChange={(e) => onCustomDateChange('start', e.target.value)}
//                                 style={{ flex: 1, background: isDark ? '#252d3d' : '#fff', border: `1px solid ${borderCol}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: textMain, fontFamily: 'inherit', outline: 'none' }}
//                             />
//                             <input
//                                 type="date"
//                                 value={formatDateForInput(endDate)}
//                                 min={formatDateForInput(startDate)}
//                                 max={formatDateForInput(new Date())}
//                                 onChange={(e) => onCustomDateChange('end', e.target.value)}
//                                 style={{ flex: 1, background: isDark ? '#252d3d' : '#fff', border: `1px solid ${borderCol}`, borderRadius: 6, padding: '6px 8px', fontSize: 12, color: textMain, fontFamily: 'inherit', outline: 'none' }}
//                             />
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// /* ─── StatusPill ─────────────────────────────────────────── */
// const StatusPill = ({ status }) => {
//     const cfg = STATUS_CONFIG[status] || { label: status, color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' };
//     return (
//         <span style={{
//             padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
//             letterSpacing: '0.04em', textTransform: 'uppercase',
//             color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
//             whiteSpace: 'nowrap',
//         }}>
//             {cfg.label}
//         </span>
//     );
// };

// /* ─── Items Expandable ───────────────────────────────────── */
// const ItemsRow = ({ items, status }) => {
//     const [open, setOpen] = useState(false);
//     const showReceivedCols = status === 'RECEIVED' || status === 'CLOSED';

//     return (
//         <div style={{ marginTop: 10 }}>
//             <button onClick={() => setOpen(o => !o)} style={{
//                 background: 'none', border: 'none', padding: 0, cursor: 'pointer',
//                 display: 'inline-flex', alignItems: 'center', gap: 6,
//                 color: '#6366f1', fontSize: 13, fontWeight: 600,
//             }}>
//                 <span style={{
//                     display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
//                     width: 20, height: 20, borderRadius: 6,
//                     background: '#eef2ff', color: '#6366f1', fontSize: 11, fontWeight: 800,
//                 }}>{items.length}</span>
//                 Item{items.length !== 1 ? 's' : ''}
//                 <span style={{ fontSize: 9, display: 'inline-block', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
//             </button>

//             {open && (
//                 <div style={{ marginTop: 8, border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
//                     <div style={{
//                         display: 'grid',
//                         gridTemplateColumns: showReceivedCols ? '2.5fr 1fr 1fr 1fr 130px 130px 130px' : '2.5fr 1fr 1fr 1fr 140px',
//                         background: '#f9fafb', padding: '7px 14px', borderBottom: '1px solid #e5e7eb',
//                         fontSize: 11, fontWeight: 700, color: '#6b7280',
//                         letterSpacing: '0.05em', textTransform: 'uppercase',
//                     }}>
//                         <span>Material</span>
//                         <span>Category</span>
//                         <span>HSN</span>
//                         <span>Unit</span>
//                         <span>Requested Qty</span>
//                         {showReceivedCols && <span>Received Qty</span>}
//                         {showReceivedCols && <span>Balance Qty</span>}
//                     </div>

//                     {items.map((item, idx) => {
//                         const rm = item.rawMaterial || {};
//                         const requested = Number(item.requestedQuantity || 0);
//                         const received = Number(item.totalReceivedQuantity || 0);
//                         const balance = requested - received;
//                         return (
//                             <div key={item._id || idx} style={{
//                                 display: 'grid',
//                                 gridTemplateColumns: showReceivedCols ? '2.5fr 1fr 1fr 1fr 130px 130px 130px' : '2.5fr 1fr 1fr 1fr 130px',
//                                 padding: '9px 14px', alignItems: 'center',
//                                 background: idx % 2 === 0 ? '#fff' : '#fafafa',
//                                 borderBottom: idx < items.length - 1 ? '1px solid #f3f4f6' : 'none',
//                                 fontSize: 13,
//                             }}>
//                                 <div>
//                                     <div style={{ fontWeight: 600, color: '#111827' }}>{rm.name || '—'}</div>
//                                     {rm.description && (
//                                         <div style={{ color: '#9ca3af', fontSize: 11, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300 }}>
//                                             {rm.description}
//                                         </div>
//                                     )}
//                                 </div>
//                                 <span style={{ color: '#373b41', fontSize: 12 }}>
//                                     {rm?.subCategoryName}
//                                     <div style={{ color: '#6b7280', fontSize: 12 }}>{rm?.mainCategoryName || '—'}</div>
//                                 </span>
//                                 <span style={{ color: '#6b7280', fontFamily: 'monospace', fontSize: 12 }}>{rm?.hsn || '—'}</span>
//                                 <span style={{ color: '#6b7280', fontSize: 12 }}>{rm?.unit || '—'}</span>
//                                 <span style={{ textAlign: 'left', fontWeight: 700, color: '#6366f1', fontSize: 14 }}>
//                                     {requested.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
//                                 </span>
//                                 {showReceivedCols && (
//                                     <span style={{ textAlign: 'left', fontWeight: 700, color: '#059669', fontSize: 14 }}>
//                                         {received.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
//                                     </span>
//                                 )}
//                                 {showReceivedCols && (
//                                     <span style={{ textAlign: 'left', fontWeight: 700, color: balance > 0 ? '#dc2626' : '#059669', fontSize: 14 }}>
//                                         {balance.toFixed(2)} <span style={{ fontSize: 12, fontWeight: 500 }}>{rm?.unit || ''}</span>
//                                     </span>
//                                 )}
//                             </div>
//                         );
//                     })}
//                 </div>
//             )}
//         </div>
//     );
// };

// /* ─── Update Modal ───────────────────────────────────────── */
// const UpdateModal = ({ request, onHide, onUpdated, accessToken }) => {
//     const [status, setStatus] = useState('ACCEPTED');
//     const [saving, setSaving] = useState(false);
//     const [error, setError] = useState('');

//     const alreadyUpdated = request?.status === 'ACCEPTED' || request?.status === 'REJECTED';

//     const handleSave = async () => {
//         setSaving(true); setError('');
//         try {
//             const headers = { Authorization: `Bearer ${accessToken}` };
//             await axiosInstance.put(`stock-requisition/update-status`, { _id: request._id, status }, { headers });
//             onUpdated(request._id, status);
//             onHide();
//         } catch (error) {
//             if (error?.response) {
//                 let errorMessage = '';
//                 if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 400) errorMessage = error?.response?.data?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 500) errorMessage = error?.response?.data?.error || error?.response?.data?.message;
//                 setError(errorMessage || 'Failed to update. Please try again.');
//             }
//         } finally {
//             setSaving(false);
//         }
//     };

//     return (
//         <div style={{
//             position: 'fixed', inset: 0, zIndex: 1050,
//             background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//         }} onClick={onHide}>
//             <div style={{
//                 background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
//                 margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
//             }} onClick={e => e.stopPropagation()}>

//                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
//                     <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Update Stock Request</span>
//                     <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0 }}>×</button>
//                 </div>

//                 <div style={{ padding: '20px' }}>
//                     <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                         <div>
//                             <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Indent Number</div>
//                             <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>{request.indentNumber}</div>
//                         </div>
//                         <div style={{ textAlign: 'right' }}>
//                             <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</div>
//                             <StatusPill status={request.status} />
//                         </div>
//                     </div>

//                     {alreadyUpdated ? (
//                         <div>
//                             <div style={{ padding: '16px', borderRadius: 9, background: '#f9fafb', border: '1px solid #e5e7eb', textAlign: 'center', color: '#6b7280', fontSize: 13, lineHeight: 1.6 }}>
//                                 This request has already been <strong style={{ color: '#374151' }}>{request.status.toLowerCase()}</strong>. You can still change the status below.
//                             </div>
//                             <div className='mt-3' style={{ display: 'flex', gap: 10 }}>
//                                 {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
//                                     <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`, background: status === key ? cfg.bg : '#fff', transition: 'all .15s', textAlign: 'center' }}>
//                                         <input type="radio" name="status" value={key} checked={status === key} onChange={() => setStatus(key)} style={{ accentColor: cfg.color, width: 15, height: 15 }} />
//                                         <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
//                                     </label>
//                                 ))}
//                             </div>
//                         </div>
//                     ) : (
//                         <>
//                             <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 700, color: '#374151' }}>
//                                 Set Status <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(choose one)</span>
//                             </div>
//                             <div style={{ display: 'flex', gap: 10 }}>
//                                 {Object.entries(UPDATABLE_STATUSES).map(([key, cfg]) => (
//                                     <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 14px', borderRadius: 10, cursor: 'pointer', border: `2px solid ${status === key ? cfg.border : '#e5e7eb'}`, background: status === key ? cfg.bg : '#fff', transition: 'all .15s', textAlign: 'center' }}>
//                                         <input type="radio" name="status" value={key} checked={status === key} onChange={() => setStatus(key)} style={{ accentColor: cfg.color, width: 15, height: 15 }} />
//                                         <span style={{ fontWeight: 700, fontSize: 13, color: cfg.color }}>{cfg.label}</span>
//                                     </label>
//                                 ))}
//                             </div>
//                         </>
//                     )}

//                     {error && (
//                         <div style={{ marginTop: 12, padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>{error}</div>
//                     )}
//                 </div>

//                 <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
//                     <button onClick={onHide} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>
//                         {alreadyUpdated ? 'Close' : 'Cancel'}
//                     </button>
//                     <button onClick={handleSave} disabled={saving} style={{ background: saving ? '#a5b4fc' : '#6366f1', border: 'none', borderRadius: 8, padding: '8px 22px', fontSize: 13, fontWeight: 700, color: '#fff', cursor: saving ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit' }}>
//                         {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save Changes'}
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// };

// /* ─── Ready-to-Pick Modal ─────────────────────────────────── */
// const ReadyToPickModal = ({ request, onHide, onUpdated, accessToken }) => {
//     const [saving, setSaving] = useState(false);
//     const [error, setError] = useState('');

//     const [storageLocations, setStorageLocations] = useState([]);
//     const [loadingLocations, setLoadingLocations] = useState(false);
//     const [selectedStorageLocation, setSelectedStorageLocation] = useState(null);

//     useEffect(() => {
//         const fetchStorageLocations = async () => {
//             setLoadingLocations(true);
//             try {
//                 const headers = { Authorization: `Bearer ${accessToken}` };
//                 const response = await axiosInstance.get('storage-location', { headers });
//                 if (response?.data?.data?.storageLocations) {
//                     setStorageLocations(response.data.data.storageLocations);
//                 }
//             } catch (err) {
//                 // silently ignore; the select will just show no options
//             } finally {
//                 setLoadingLocations(false);
//             }
//         };
//         fetchStorageLocations();
//     }, [accessToken]);

//     const storageLocationOptions = storageLocations.map(loc => ({
//         value: loc._id,
//         label: loc.name,
//         sublabel: loc.city,
//         data: loc,
//     }));

//     const formatOptionLabel = ({ label, sublabel }) => (
//         <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//             <span style={{ fontWeight: 700, fontSize: 13 }}>{label}</span>
//             {sublabel && <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>📍 {sublabel}</span>}
//         </div>
//     );

//     const selectStyles = {
//         control: (base, state) => ({
//             ...base,
//             background: '#fff',
//             borderColor: state.isFocused ? '#0891b2' : '#e5e7eb',
//             borderWidth: 1.5,
//             borderRadius: 9,
//             minHeight: 42,
//             boxShadow: state.isFocused ? '0 0 0 3px rgba(8,145,178,0.15)' : 'none',
//             '&:hover': { borderColor: '#0891b2' },
//             fontFamily: 'inherit',
//             fontSize: 13,
//             fontWeight: 600,
//         }),
//         menu: (base) => ({
//             ...base,
//             background: '#fff',
//             border: '1.5px solid #e5e7eb',
//             borderRadius: 10,
//             boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
//             zIndex: 9999,
//             fontFamily: 'inherit',
//         }),
//         menuPortal: (base) => ({ ...base, zIndex: 9999 }),
//         option: (base, state) => ({
//             ...base,
//             background: state.isSelected ? '#0891b2' : state.isFocused ? '#ecfeff' : 'transparent',
//             color: state.isSelected ? '#fff' : '#111827',
//             fontSize: 13,
//             fontWeight: state.isSelected ? 700 : 500,
//             padding: '10px 14px',
//             cursor: 'pointer',
//         }),
//         singleValue: (base) => ({ ...base, color: '#111827', fontWeight: 600, fontSize: 13 }),
//         placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: 13 }),
//         indicatorSeparator: () => ({ display: 'none' }),
//     };

//     const handleConfirm = async () => {
//         if (!selectedStorageLocation) {
//             setError('Please select a storage location before continuing.');
//             return;
//         }
//         setSaving(true); setError('');
//         try {
//             const headers = { Authorization: `Bearer ${accessToken}` };
//             await axiosInstance.put(
//                 `stock-requisition/update-status-ready-to-pick`,
//                 { _id: request._id, status: 'READY-TO-PICK', storageLocation: selectedStorageLocation.value },
//                 { headers }
//             );
//             onUpdated(request._id, 'READY_TO_PICK');
//             onHide();
//         } catch (error) {
//             if (error?.response) {
//                 let errorMessage = '';
//                 if (error.response?.status === 406) errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 400) errorMessage = error?.response?.data?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 412) errorMessage = error?.response?.data?.data?.message || error?.response?.data?.error;
//                 else if (error.response?.status === 500) errorMessage = error?.response?.data?.error || error?.response?.data?.message;
//                 setError(errorMessage || 'Failed to update. Please try again.');
//             } else {
//                 setError('Failed to update. Please try again.');
//             }
//         } finally {
//             setSaving(false);
//         }
//     };

//     return (
//         <div style={{
//             position: 'fixed', inset: 0, zIndex: 1050,
//             background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
//             display: 'flex', alignItems: 'center', justifyContent: 'center',
//         }} onClick={onHide}>
//             <div style={{
//                 background: '#fff', borderRadius: 14, width: '100%', maxWidth: 420,
//                 margin: '0 16px', boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
//             }} onClick={e => e.stopPropagation()}>

//                 <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f3f4f6' }}>
//                     <span style={{ fontWeight: 800, fontSize: 16, color: '#111827' }}>Mark as Ready to Pick</span>
//                     <button onClick={onHide} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#9ca3af', lineHeight: 1, padding: 0 }}>×</button>
//                 </div>

//                 <div style={{ padding: '20px' }}>
//                     <div style={{ marginBottom: 16, padding: '10px 14px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                         <div>
//                             <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Indent Number</div>
//                             <div style={{ fontWeight: 700, color: '#6366f1', fontFamily: 'monospace', fontSize: 14 }}>{request.indentNumber}</div>
//                         </div>
//                         <div style={{ textAlign: 'right' }}>
//                             <div style={{ fontSize: 11, color: '#9ca3af', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Current Status</div>
//                             <StatusPill status={request.status === 'IN-PRODUCTION' ? 'IN_PRODUCTION' : request.status} />
//                         </div>
//                     </div>

//                     <div style={{ marginBottom: 16 }}>
//                         <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
//                             <span style={{ fontSize: 13, fontWeight: 700, color: '#374151' }}>Storage Location</span>
//                             <span style={{
//                                 fontSize: 10, color: '#ef4444', fontWeight: 700,
//                                 background: '#fee2e2', padding: '2px 6px', borderRadius: 5,
//                             }}>REQUIRED</span>
//                         </div>
//                         <Select
//                             options={storageLocationOptions}
//                             value={selectedStorageLocation}
//                             onChange={setSelectedStorageLocation}
//                             formatOptionLabel={formatOptionLabel}
//                             placeholder={loadingLocations ? 'Loading storage locations…' : 'Select storage location…'}
//                             isLoading={loadingLocations}
//                             styles={selectStyles}
//                             isClearable
//                             isSearchable
//                             noOptionsMessage={() => 'No storage locations found'}
//                             menuPortalTarget={document.body}
//                             menuPosition="fixed"
//                         />
//                     </div>

//                     <div style={{ padding: '14px 16px', borderRadius: 9, background: '#ecfeff', border: '1px solid #a5f3fc', color: '#0e7490', fontSize: 13, lineHeight: 1.6 }}>
//                         This will mark indent <strong>{request.indentNumber}</strong> as <strong>Ready to Pick</strong> from the selected storage location. Are you sure you want to continue?
//                     </div>

//                     {error && (
//                         <div style={{ marginTop: 12, padding: '10px 14px', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>{error}</div>
//                     )}
//                 </div>

//                 <div style={{ padding: '12px 20px', borderTop: '1px solid #f3f4f6', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
//                     <button onClick={onHide} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, color: '#374151', cursor: 'pointer', fontFamily: 'inherit' }}>
//                         Cancel
//                     </button>
//                     <button
//                         onClick={handleConfirm}
//                         disabled={saving || !selectedStorageLocation}
//                         style={{
//                             background: (saving || !selectedStorageLocation) ? '#67e8f9' : '#0891b2',
//                             border: 'none', borderRadius: 8, padding: '8px 22px',
//                             fontSize: 13, fontWeight: 700, color: '#fff',
//                             cursor: (saving || !selectedStorageLocation) ? 'not-allowed' : 'pointer',
//                             display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
//                         }}
//                     >
//                         {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : <>🏷️ Mark Ready to Pick</>}
//                     </button>
//                 </div>
//             </div>
//         </div>
//     );
// };

// /* ─── Request Card ───────────────────────────────────────── */
// const RequestCard = ({ req, onUpdate, onReadyToPick, isDark }) => {
//     const cardBg = isDark ? '#1a2132' : '#fff';
//     const border = isDark ? '#2d3748' : '#e5e7eb';
//     const text = isDark ? '#f3f4f6' : '#111827';
//     const muted = isDark ? '#9ca3af' : '#6b7280';
//     const isAccepted = req.status === 'ACCEPTED';
//     const isInProduction = req.status === 'IN-PRODUCTION';

//     return (
//         <div style={{
//             background: cardBg, border: `1px solid ${border}`, borderRadius: 10,
//             padding: '16px 18px', marginBottom: 12,
//             boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)',
//             transition: 'box-shadow .2s',
//         }}
//             onMouseEnter={e => e.currentTarget.style.boxShadow = isDark ? '0 4px 18px rgba(0,0,0,0.45)' : '0 4px 14px rgba(0,0,0,0.09)'}
//             onMouseLeave={e => e.currentTarget.style.boxShadow = isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.05)'}
//         >
//             <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
//                 <div>
//                     <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
//                         <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#6366f1' }}>{req.indentNumber}</span>
//                         <StatusPill status={req.status === 'IN-PRODUCTION' ? 'IN_PRODUCTION' : req.status === 'READY-TO-PICK' ? 'READY_TO_PICK' : req.status} />
//                         <span style={{ fontSize: 11, color: muted, background: isDark ? '#252d3d' : '#f3f4f6', padding: '2px 8px', borderRadius: 6, fontWeight: 600 }}>
//                             FY {req.fin_year}
//                         </span>
//                     </div>
//                     <div style={{ fontSize: 12, color: muted, marginTop: 4 }}>
//                         Outlet: <span style={{ color: text, fontWeight: 500 }}>{req?.outlet?.name || req?.outlet?._id || '—'}, {req?.outlet?.city}</span>
//                     </div>
//                 </div>

//                 <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
//                     <div style={{ textAlign: 'right', fontSize: 12, color: muted }}>
//                         <div>Raised on: <span style={{ color: text }}>{fmt(req.createdAt)}</span></div>
//                     </div>

//                     {req.status !== 'IN-PRODUCTION' && req.status !== 'READY-TO-PICK' && req.status !== 'RECEIVED' && req.status !== 'CLOSED' && (
//                         <button onClick={() => onUpdate(req)} style={{
//                             background: isAccepted ? '#d1fae5' : '#f5f3ff',
//                             border: `1px solid ${isAccepted ? '#6ee7b7' : '#ddd6fe'}`,
//                             borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 700,
//                             color: isAccepted ? '#059669' : '#7c3aed',
//                             cursor: 'pointer', whiteSpace: 'nowrap',
//                             display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', transition: 'background .15s',
//                         }}
//                             onMouseEnter={e => e.currentTarget.style.background = isAccepted ? '#a7f3d0' : '#ede9fe'}
//                             onMouseLeave={e => e.currentTarget.style.background = isAccepted ? '#d1fae5' : '#f5f3ff'}
//                         >
//                             {isAccepted ? '📦 Take To Production' : '✎ Update'}
//                         </button>
//                     )}

//                     {isInProduction && (
//                         <button onClick={() => onReadyToPick(req)} style={{
//                             background: '#cffafe',
//                             border: '1px solid #67e8f9',
//                             borderRadius: 7, padding: '6px 13px', fontSize: 12, fontWeight: 700,
//                             color: '#0891b2',
//                             cursor: 'pointer', whiteSpace: 'nowrap',
//                             display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit', transition: 'background .15s',
//                         }}
//                             onMouseEnter={e => e.currentTarget.style.background = '#a5f3fc'}
//                             onMouseLeave={e => e.currentTarget.style.background = '#cffafe'}
//                         >
//                             🏷️ Ready to Pick
//                         </button>
//                     )}

//                     {/* Download Dropdown per card */}
//                     <DownloadDropdown
//                         label="📄 Download"
//                         isDark={isDark}
//                         onPDF={() => handleDownloadPDF(req)}
//                         onExcel={() => handleDownloadExcel(req)}
//                     />
//                 </div>
//             </div>

//             <ItemsRow items={req.items || []} status={req.status} />
//         </div>
//     );
// };

// /* ─── Pagination ─────────────────────────────────────────── */
// const Pagination = ({ currentPage, totalPages, onPageChange, isDark }) => {
//     const borderCol = isDark ? '#2d3748' : '#e5e7eb';
//     const textMain = isDark ? '#f3f4f6' : '#111827';
//     const textMuted = isDark ? '#9ca3af' : '#6b7280';

//     const getPageNumbers = () => {
//         const pages = [];
//         const maxVisible = 5;
//         if (totalPages <= maxVisible) {
//             for (let i = 1; i <= totalPages; i++) pages.push(i);
//         } else if (currentPage <= 3) {
//             pages.push(1, 2, 3, 4, '...', totalPages);
//         } else if (currentPage >= totalPages - 2) {
//             pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
//         } else {
//             pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
//         }
//         return pages;
//     };

//     const btnBase = {
//         border: `1px solid ${borderCol}`, borderRadius: 7, padding: '6px 12px',
//         fontSize: 13, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
//     };

//     return (
//         <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
//             <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ ...btnBase, background: isDark ? '#1e293b' : '#fff', color: currentPage === 1 ? textMuted : textMain, opacity: currentPage === 1 ? 0.5 : 1 }}>← Previous</button>
//             {getPageNumbers().map((page, idx) => (
//                 page === '...'
//                     ? <span key={`e-${idx}`} style={{ color: textMuted, padding: '0 4px' }}>...</span>
//                     : <button key={page} onClick={() => onPageChange(page)} style={{ ...btnBase, background: currentPage === page ? '#6366f1' : (isDark ? '#1e293b' : '#fff'), border: `1px solid ${currentPage === page ? '#6366f1' : borderCol}`, color: currentPage === page ? '#fff' : textMain, fontWeight: currentPage === page ? 700 : 600, minWidth: 36 }}>{page}</button>
//             ))}
//             <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ ...btnBase, background: isDark ? '#1e293b' : '#fff', color: currentPage === totalPages ? textMuted : textMain, opacity: currentPage === totalPages ? 0.5 : 1 }}>Next →</button>
//         </div>
//     );
// };

// /* ─── Main Component ─────────────────────────────────────── */
// const StocksRequest = () => {
//     const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
//     const [skin, setSkin] = useState(currentSkin);
//     const accessToken = localStorage.getItem('authToken');

//     const [stockRequests, setStockRequests] = useState([]);
//     const [selectedOutlet, setSelectedOutlet] = useState(null);
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState('');
//     const [searchTerm, setSearchTerm] = useState('');
//     const [statusFilter, setStatusFilter] = useState('ALL');
//     const [updateTarget, setUpdateTarget] = useState(null);
//     const [readyToPickTarget, setReadyToPickTarget] = useState(null);

//     const [currentPage, setCurrentPage] = useState(1);
//     const [totalPages, setTotalPages] = useState(1);
//     const [totalRecords, setTotalRecords] = useState(0);
//     const [statusCounts, setStatusCounts] = useState({});

//     // ── Date filter state (defaults to Last 30 Days) ──
//     const [dateFilterKey, setDateFilterKey] = useState('LAST_30_DAYS');
//     const initialRange = getDateRangeForOption('LAST_30_DAYS');
//     const [startDate, setStartDate] = useState(initialRange.start); // Date object
//     const [endDate, setEndDate] = useState(initialRange.end);       // Date object

//     // Debounce timer for the search box so we don't hit the API on every keystroke
//     const searchDebounceRef = useRef(null);
//     useEffect(() => {
//         return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
//     }, []);

//     const navigate = useNavigate();

//     const isDark = skin === 'dark';
//     const pageBg = isDark ? '#111827' : '#f3f4f6';
//     const cardBg = isDark ? '#1a2132' : '#fff';
//     const textMain = isDark ? '#f3f4f6' : '#111827';
//     const textMuted = isDark ? '#9ca3af' : '#6b7280';
//     const borderCol = isDark ? '#2d3748' : '#e5e7eb';

//     const getStockRequests = async (page = 1, filters = {}) => {
//         setLoading(true); setError('');
//         try {
//             const headers = { Authorization: `Bearer ${accessToken}` };
//             const params = new URLSearchParams();
//             params.append('page', page);
//             params.append('limit', 30);

//             const outletId = filters.outlet?.value || filters.outlet?._id || '';
//             if (outletId) params.append('outlet', outletId);

//             const STATUS_API_MAPPING = {
//                 IN_PRODUCTION: 'IN-PRODUCTION',
//                 READY_TO_PICK: 'READY-TO-PICK',
//             };

//             const status = filters.status !== undefined ? filters.status : statusFilter;

//             if (status && status !== 'ALL') {
//                 params.append(
//                     'status',
//                     STATUS_API_MAPPING[status] || status
//                 );
//             }

//             const start = filters.startDate !== undefined ? filters.startDate : startDate;
//             const end = filters.endDate !== undefined ? filters.endDate : endDate;
//             if (start) params.append('startDate', formatPayloadDate(start));
//             if (end) params.append('endDate', formatPayloadDate(end));

//             // NEW — search by indent number or raw material name (handled by backend)
//             const search = filters.search !== undefined ? filters.search : searchTerm;
//             if (search && search.trim()) params.append('search', search.trim());

//             const res = await axiosInstance.get(`stock-requisition?${params.toString()}`, { headers });
//             const responseData = res?.data?.data || {};
//             const requests = responseData.data || [];
//             const pagination = responseData.pagination || {};
//             const counts = responseData.statusCounts || {};

//             const sortedData = requests.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
//             setStockRequests(sortedData);
//             setCurrentPage(pagination.currentPage || 1);
//             setTotalPages(pagination.totalPages || 1);
//             setTotalRecords(pagination.totalRecords || 0);
//             setStatusCounts(counts);
//         } catch (err) {
//             console.log("Error in Get Stock Requestes Api :", err);
//             setStockRequests([]);
//             setCurrentPage(1);
//             setTotalPages(1);
//             setTotalRecords(0);
//             setStatusCounts(err?.response?.data?.statusCounts || {});

//             // Treat a "no match found" response as an empty result rather than a hard error,
//             // so the UI can show "No records found" instead of a generic error banner.
//             const httpStatus = err?.response?.status;
//             if (httpStatus === 404) {
//                 setError('');
//             } else {
//                 setError('There are no stock requests at the moment.');
//             }
//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleUpdateClick = (req) => {
//         if (req.status === 'ACCEPTED') {
//             navigate('/purchase/stock-transfer', { state: { request: req } });
//         } else {
//             setUpdateTarget(req);
//         }
//     };

//     const handleReadyToPickClick = (req) => {
//         setReadyToPickTarget(req);
//     };

//     useEffect(() => { getStockRequests(1, { startDate, endDate }); }, []);

//     const handleOutletChange = (option) => {
//         setSelectedOutlet(option);
//         setCurrentPage(1);
//         getStockRequests(1, { outlet: option, search: searchTerm });
//     };

//     const handleStatusChange = (newStatus) => {
//         setStatusFilter(newStatus);
//         setCurrentPage(1);
//         getStockRequests(1, { status: newStatus, outlet: selectedOutlet, search: searchTerm });
//     };

//     // Debounced search — sends `search` (indent number OR raw material name) to the backend
//     const handleSearchChange = (value) => {
//         setSearchTerm(value);
//         if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
//         searchDebounceRef.current = setTimeout(() => {
//             setCurrentPage(1);
//             getStockRequests(1, {
//                 search: value,
//                 outlet: selectedOutlet,
//                 status: statusFilter,
//                 startDate,
//                 endDate,
//             });
//         }, 400);
//     };

//     const handlePresetSelect = (key) => {
//         setDateFilterKey(key);
//         if (key === 'CUSTOM') return; // wait for user to pick custom dates
//         const { start, end } = getDateRangeForOption(key);
//         setStartDate(start);
//         setEndDate(end);
//         setCurrentPage(1);
//         getStockRequests(1, { startDate: start, endDate: end, outlet: selectedOutlet, search: searchTerm });
//     };

//     const handleCustomDateChange = (type, value) => {
//         // value comes from <input type="date"> as 'YYYY-MM-DD'
//         const parsed = value ? new Date(value + 'T00:00:00') : null;
//         setDateFilterKey('CUSTOM');
//         if (type === 'start') {
//             setStartDate(parsed);
//             setCurrentPage(1);
//             getStockRequests(1, { startDate: parsed, endDate, outlet: selectedOutlet, search: searchTerm });
//         } else {
//             setEndDate(parsed);
//             setCurrentPage(1);
//             getStockRequests(1, { startDate, endDate: parsed, outlet: selectedOutlet, search: searchTerm });
//         }
//     };

//     const handlePageChange = (page) => {
//         if (page >= 1 && page <= totalPages) {
//             setCurrentPage(page);
//             getStockRequests(page, { search: searchTerm, outlet: selectedOutlet, status: statusFilter, startDate, endDate });
//             window.scrollTo({ top: 0, behavior: 'smooth' });
//         }
//     };
//     const handleUpdated = () => getStockRequests(currentPage, { search: searchTerm, outlet: selectedOutlet, status: statusFilter, startDate, endDate });
//     const handleReadyToPickUpdated = () => getStockRequests(currentPage, { search: searchTerm, outlet: selectedOutlet, status: statusFilter, startDate, endDate });
//     const getAllCount = () => Object.values(statusCounts).reduce((sum, count) => sum + count, 0);

//     const showCumulativeBtn = (statusFilter === 'PENDING' || statusFilter === 'ACCEPTED') && stockRequests.length > 0;

//     return (
//         <React.Fragment>
//             <link href="https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
//             <Header onSkin={setSkin} />

//             {updateTarget && (
//                 <UpdateModal request={updateTarget} onHide={() => setUpdateTarget(null)} onUpdated={handleUpdated} accessToken={accessToken} />
//             )}

//             {readyToPickTarget && (
//                 <ReadyToPickModal request={readyToPickTarget} onHide={() => setReadyToPickTarget(null)} onUpdated={handleReadyToPickUpdated} accessToken={accessToken} />
//             )}

//             <div className={`main main-app p-3 p-lg-3 ${skin === 'dark' ? 'bg-dark text-light' : 'bg-light'}`}
//                 style={{ minHeight: '100vh', background: pageBg, fontFamily: "'Nunito', 'Segoe UI', sans-serif" }}>
//                 <Container fluid>

//                     {/* ── Page Header ── */}
//                     <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
//                         <div>
//                             <h4 style={{ fontWeight: 800, fontSize: 21, margin: 0, color: textMain, letterSpacing: '-0.01em' }}>Stock Requests</h4>
//                             <p style={{ color: textMuted, margin: '3px 0 0', fontSize: 13 }}>Manage and track material requisitions from outlets</p>
//                         </div>
//                         <button onClick={() => getStockRequests(currentPage, { search: searchTerm, outlet: selectedOutlet, status: statusFilter, startDate, endDate })} disabled={loading} style={{
//                             background: '#6366f1', color: '#fff', border: 'none', borderRadius: 9,
//                             padding: '9px 18px', fontWeight: 700, fontSize: 13,
//                             cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
//                             display: 'flex', alignItems: 'center', gap: 7, fontFamily: 'inherit',
//                         }}>
//                             {loading ? <><Spinner animation="border" size="sm" /> Refreshing…</> : <><span style={{ fontSize: 15 }}>↺</span> Refresh</>}
//                         </button>
//                     </div>

//                     {/* ── Filters ── */}
//                     <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '16px', marginBottom: 14 }}>
//                         <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1.3fr 1.5fr', gap: 12 }}>
//                             <Form.Group controlId="outletSelect" style={{ margin: 0 }}>
//                                 <OutletSelect value={selectedOutlet} onChange={handleOutletChange} required={false} />
//                             </Form.Group>

//                             <DateRangeDropdown
//                                 dateFilterKey={dateFilterKey}
//                                 startDate={startDate}
//                                 endDate={endDate}
//                                 onPresetSelect={handlePresetSelect}
//                                 onCustomDateChange={handleCustomDateChange}
//                                 isDark={isDark}
//                             />

//                             <div>
//                                 <Form.Label style={{ color: textMuted, marginBottom: 6, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
//                                     <i className="bi bi-search me-2"></i>Search
//                                 </Form.Label>
//                                 <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: isDark ? '#252d3d' : '#f9fafb', border: `1px solid ${borderCol}`, borderRadius: 8, padding: '8px 12px' }}>
//                                     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={textMuted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
//                                         <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
//                                     </svg>
//                                     <input
//                                         value={searchTerm}
//                                         onChange={e => handleSearchChange(e.target.value)}
//                                         placeholder="Search by indent no. or material…"
//                                         style={{ border: 'none', background: 'transparent', flex: 1, outline: 'none', color: textMain, fontSize: 13, fontFamily: 'inherit' }}
//                                     />
//                                     {searchTerm && (
//                                         <button onClick={() => handleSearchChange('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: textMuted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
//                                     )}
//                                 </div>
//                             </div>
//                         </div>
//                     </div>

//                     {/* ── Status Filter Chips + Cumulative Download ── */}
//                     {!loading && (
//                         <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center', justifyContent: 'space-between' }}>
//                             <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
//                                 {[{ key: 'ALL', label: 'All' }, ...Object.keys(STATUS_CONFIG).map(k => ({ key: k, label: STATUS_CONFIG[k].label }))].map(({ key, label }) => {
//                                     const count = key === 'ALL' ? getAllCount() : (statusCounts[STATUS_COUNT_KEY_MAP[key]] || 0);

//                                     const cfg = STATUS_CONFIG[key] || {};
//                                     const active = statusFilter === key;
//                                     return (
//                                         <button key={key} onClick={() => handleStatusChange(key)} style={{
//                                             border: `1.5px solid ${active ? (cfg.border || '#a5b4fc') : borderCol}`,
//                                             background: active ? (cfg.bg || '#eef2ff') : cardBg,
//                                             color: active ? (cfg.color || '#6366f1') : textMuted,
//                                             borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700,
//                                             cursor: 'pointer', transition: 'all .15s', fontFamily: 'inherit',
//                                         }}>
//                                             {label} ({count})
//                                         </button>
//                                     );
//                                 })}
//                             </div>

//                             {/* Cumulative Download + Shortage Report */}
//                             {!loading && stockRequests.length > 0 && (
//                                 <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
//                                     {showCumulativeBtn && (
//                                         <DownloadDropdown
//                                             label="📄 Cumulative Download"
//                                             isDark={isDark}
//                                             onPDF={() => handleDownloadCombinedPDF(stockRequests)}
//                                             onExcel={() => handleDownloadCombinedExcel(stockRequests)}
//                                         />
//                                     )}

//                                     {(statusFilter === 'RECEIVED' || statusFilter === 'CLOSED') && stockRequests.length > 0 && (
//                                         <button
//                                             onClick={() => handleDownloadShortageExcel(stockRequests)}
//                                             style={{
//                                                 background: '#fff7ed',
//                                                 border: '1px solid #fed7aa',
//                                                 borderRadius: 7,
//                                                 padding: '6px 13px',
//                                                 fontSize: 12,
//                                                 fontWeight: 700,
//                                                 color: '#ea580c',
//                                                 cursor: 'pointer',
//                                                 whiteSpace: 'nowrap',
//                                                 display: 'flex',
//                                                 alignItems: 'center',
//                                                 gap: 5,
//                                                 fontFamily: 'inherit',
//                                                 transition: 'background .15s',
//                                             }}
//                                             onMouseEnter={e => e.currentTarget.style.background = '#fed7aa'}
//                                             onMouseLeave={e => e.currentTarget.style.background = '#fff7ed'}
//                                         >
//                                             ⚠️ Shortage Report
//                                         </button>
//                                     )}
//                                 </div>
//                             )}
//                         </div>
//                     )}

//                     {/* ── Error ── */}
//                     {error && (
//                         <div style={{ background: '#e4e2fe', border: '1px solid #a5a6fc', borderRadius: 9, padding: '11px 15px', marginBottom: 14, color: '#262fdc', fontSize: 13 }}>{error}</div>
//                     )}

//                     {/* ── Loading Skeleton ── */}
//                     {loading && (
//                         <>
//                             <style>{`@keyframes shimmer{0%,100%{opacity:1}50%{opacity:.4}}`}</style>
//                             {[1, 2, 3].map(i => (
//                                 <div key={i} style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '16px 18px', marginBottom: 12, animation: 'shimmer 1.5s ease-in-out infinite' }}>
//                                     <div style={{ height: 14, width: '32%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5, marginBottom: 10 }} />
//                                     <div style={{ height: 11, width: '20%', background: isDark ? '#2d3748' : '#e5e7eb', borderRadius: 5 }} />
//                                 </div>
//                             ))}
//                         </>
//                     )}

//                     {/* ── Empty State (covers "no records at all" AND "no results for search") ── */}
//                     {!loading && !error && stockRequests.length === 0 && (
//                         <div style={{ background: cardBg, border: `1px solid ${borderCol}`, borderRadius: 10, padding: '52px 20px', textAlign: 'center' }}>
//                             <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
//                             <div style={{ fontWeight: 800, fontSize: 15, color: textMain, marginBottom: 5 }}>
//                                 {searchTerm ? 'No records found' : 'No stock requests found'}
//                             </div>
//                             <div style={{ color: textMuted, fontSize: 13 }}>
//                                 {searchTerm
//                                     ? `No results for "${searchTerm}". Try a different indent number or material name.`
//                                     : statusFilter !== 'ALL'
//                                         ? 'Try adjusting your filters'
//                                         : selectedOutlet?.value
//                                             ? 'This outlet has no stock requests for the selected date range'
//                                             : 'Stock requisitions will appear here once raised'}
//                             </div>
//                         </div>
//                     )}

//                     {/* ── Request Cards (backend already filters by search/outlet/status/date) ── */}
//                     {!loading && stockRequests.map(req => (
//                         <RequestCard key={req._id} req={req} isDark={isDark} onUpdate={handleUpdateClick} onReadyToPick={handleReadyToPickClick} />
//                     ))}

//                     {/* ── Pagination ── */}
//                     {!loading && stockRequests.length > 0 && totalPages > 1 && (
//                         <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} isDark={isDark} />
//                     )}

//                     {/* ── Footer Info ── */}
//                     {!loading && stockRequests.length > 0 && (
//                         <div style={{ textAlign: 'center', color: textMuted, fontSize: 12, marginTop: 16, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
//                             <span>Showing {((currentPage - 1) * 20) + 1} - {Math.min(currentPage * 20, totalRecords)} of {totalRecords} request{totalRecords !== 1 ? 's' : ''}</span>
//                             <span style={{ color: borderCol }}>•</span>
//                             <span>Page {currentPage} of {totalPages}</span>
//                         </div>
//                     )}

//                 </Container>
//             </div>
//         </React.Fragment>
//     );
// };

// export default StocksRequest;